// src/utils/generateClientIllustration.js
// UPDATED VERSION - ERC table matching reference image style

import jsPDF from 'jspdf';
import { optimizeImageForPdf, pdfImageFormat } from "./pdfIllustrationImage.js";

// Font for illustration text. Register via fontFiles param or use built-in fallback (times = softer than helvetica).
const ILLUSTRATION_FONT = "BrandonGrotesque";
const ILLUSTRATION_FONT_FALLBACK = "times"; // built-in: times (serif), helvetica (sans), courier (mono)

function createIllustrationPdf() {
  return new jsPDF({ compress: true });
}

function addPdfLogoImage(doc, logoBase64, x, y, w, h) {
  doc.addImage(logoBase64, pdfImageFormat(logoBase64), x, y, w, h, "dynesourcer-logo", "MEDIUM");
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Helper to format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  try {
    return `${Number(value).toFixed(2)}%`;
  } catch {
    return String(value);
  }
};

/** IF service fee is stored as a real percent (e.g. 0.96). Values like 96 are wrongly scaled. */
const resolveIfServiceFeePercent = (...candidates) => {
  for (const value of candidates) {
    const n = firstFinite(value);
    if (n == null) continue;
    if (n > 20) return Math.round((n / 100) * 1e8) / 1e8;
    return n;
  }
  return null;
};

/** Preserve small IF percents like 0.96 without forcing 2dp noise for integers. */
const formatIfServiceFeePercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n * 1e8) / 1e8;
  if (Number.isInteger(rounded)) return `${rounded}%`;
  return `${rounded.toFixed(8).replace(/\.?0+$/, "")}%`;
};

const formatErcPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(8).replace(/\.?0+$/, "")}%`;
};

const toLenderNameOnly = (raw) => {
  const input = String(raw || "").trim();
  if (!input) return "—";
  const local = input.includes("@") ? input.split("@")[0] : input;
  const cleaned = local.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!cleaned) return "—";
  if (/^[A-Z0-9]{2,6}$/.test(cleaned)) return cleaned.toUpperCase();
  return cleaned
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const toProductTypeDisplay = (row) => {
  const raw = row?.product_type_list;
  if (Array.isArray(raw) && raw.length) {
    const normalized = raw
      .map((v) => {
        if (typeof v === "string") return v.trim();
        if (v && typeof v === "object") {
          return String(v.label || v.name || v.value || v.product_type || "").trim();
        }
        return "";
      })
      .filter(Boolean);
    if (normalized.length) return normalized.join(", ");
  }
  if (typeof row?.product_type === "string" && row.product_type.trim()) return row.product_type.trim();
  if (row?.product_type && typeof row.product_type === "object") {
    const objText = String(row.product_type.label || row.product_type.name || row.product_type.value || "").trim();
    if (objText) return objText;
  }
  return "—";
};

const toProductTermDisplay = (row) => {
  const term = row?.product_termsyears ?? row?.product_term_years ?? row?.product_term;
  if (term === null || term === undefined || term === "") return "—";
  return `${term} yrs`;
};

const resolveCalcForTable = (row) => {
  const calc = row?.calc_selected && typeof row.calc_selected === "object" ? row.calc_selected : {};
  return {
    monthly_payment:
      calc?.monthly_payment ??
      row?.monthly_payment ??
      row?.calc_interest_only?.monthly_payment ??
      row?.calc_repayment?.monthly_payment ??
      null,
    cost_over_product_term:
      calc?.cost_over_product_term ??
      row?.cost_over_product_term ??
      row?.total_cost ??
      row?.calc_interest_only?.cost_over_product_term ??
      row?.calc_repayment?.cost_over_product_term ??
      null,
    total_fees:
      calc?.total_fees ??
      row?.total_fees ??
      row?.arrangement_fee ??
      row?.product_fee_amount ??
      null,
  };
};

const safeNum = (v, fallback = 0) => {
  if (v === null || v === undefined || v === "") return fallback;
  if (typeof v === "string") {
    const s = v.replace(/[£,%\s]/g, "");
    const n = Number(s);
    return Number.isFinite(n) ? n : fallback;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const firstFinite = (...vals) => {
  for (const v of vals) {
    if (v == null || v === "") continue;
    if (typeof v === "string") {
      const s = v.trim().replace(/[£,%\s]/g, "");
      const n = Number(s);
      if (Number.isFinite(n)) return n;
      continue;
    }
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

// Helper to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  try {
    const n = safeNum(value, null);
    if (n === null) return String(value);

    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return String(value);
  }
};

// Helper to calculate arrangement fee
const resolveArrangementFeeAmount = (product, mortgageAmount) => {
  const typeRaw =
    product?.product_fee_type ||
    product?.arrangement_fee_type ||
    product?.fee_type ||
    product?.product_fee_type_selector ||
    "";

  const type = String(typeRaw || "").trim().toLowerCase();

  const feeAmount = firstFinite(
    product?.product_fee_amount,
    product?.arrangement_fee_amount,
    product?.arrangement_fee,
    product?.product_fee
  );

  const feePercent = firstFinite(
    product?.product_fee_percent,
    product?.arrangement_fee_percent,
    product?.fee_percent
  );

  const minFee = firstFinite(
    product?.min_arrangement_fee,
    product?.min_fee,
    product?.arrangement_min_fee,
    product?.product_min_fee
  );

  const mort = safeNum(mortgageAmount) || 0;

  const pctFee = () => mort * (safeNum(feePercent) / 100);
  const amtFee = () => safeNum(feeAmount);
  const minOnly = () => safeNum(minFee);

  const isPercent = type.includes("percent") || type.includes("percentage") || type === "%";
  const isMinimum = type.includes("minimum") || type.includes("min");
  const isAmount = type.includes("£") || type.includes("amount") || type.includes("fixed") || type === "gbp";

  let fee = 0;

  if (isPercent) {
    let finalPercent = feePercent;
    if (finalPercent == null && feeAmount != null && feeAmount < 100) {
      finalPercent = feeAmount;
    }

    fee = mort * (safeNum(finalPercent) / 100);
    if (Number.isFinite(Number(minFee)) && Number(minFee) > 0) {
      fee = Math.max(fee, minOnly());
    }
    return Math.max(0, safeNum(fee));
  }

  if (isMinimum) {
    if (Number.isFinite(Number(feePercent)) && Number(feePercent) > 0) {
      fee = Math.max(pctFee(), minOnly());
    } else if (Number.isFinite(Number(feeAmount)) && Number(feeAmount) > 0) {
      if (feeAmount < 100) {
        fee = Math.max(mort * (feeAmount / 100), minOnly());
      } else {
        fee = Math.max(amtFee(), minOnly());
      }
    } else {
      fee = minOnly();
    }
    return Math.max(0, safeNum(fee));
  }

  if (isAmount) {
    if (feeAmount > 0 && feeAmount <= 100) {
      const fee = mort * (feeAmount / 100);
      return Math.max(0, safeNum(fee));
    }
    return Math.max(0, amtFee());
  }

  if (feePercent && feePercent > 0) {
    fee = pctFee();
    if (minFee) fee = Math.max(fee, minOnly());
    return Math.max(0, safeNum(fee));
  }

  if (feeAmount && feeAmount > 0) {
    return Math.max(0, amtFee());
  }

  if (minFee) {
    return Math.max(0, minOnly());
  }

  return 0;
};

// Calculate monthly payment (term in years)
const calculateMonthlyPayment = (principal, annualRate, years) => {
  if (!principal || principal <= 0 || !annualRate || annualRate <= 0 || !years || years <= 0) {
    return null;
  }
  const monthlyRate = annualRate / 100 / 12;
  const numPayments = years * 12;
  if (monthlyRate > 0) {
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
  } else {
    return principal / numPayments;
  }
};

// Calculate monthly payment (term in months) — use for SVR so the same number of months shown in the PDF is used in the formula
const calculateMonthlyPaymentWithMonths = (principal, annualRate, numMonths) => {
  if (!principal || principal <= 0 || !annualRate || annualRate <= 0 || !numMonths || numMonths <= 0) {
    return null;
  }
  const n = Math.round(Number(numMonths));
  if (n <= 0) return null;
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate > 0) {
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
  }
  return principal / n;
};

const calculateInterestOnlyMonthlyPayment = (principal, annualRate) => {
  if (!principal || principal <= 0 || !annualRate || annualRate <= 0) return null;
  return principal * (annualRate / 100) / 12;
};

// Calculate remaining balance after N months
const calculateRemainingBalance = (principal, annualRate, monthlyPayment, monthsPaid) => {
  if (!principal || principal <= 0 || !annualRate || annualRate <= 0 || !monthlyPayment || monthlyPayment <= 0) {
    return principal;
  }
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate > 0) {
    return principal * Math.pow(1 + monthlyRate, monthsPaid) - monthlyPayment * (Math.pow(1 + monthlyRate, monthsPaid) - 1) / monthlyRate;
  } else {
    return principal - (monthlyPayment * monthsPaid);
  }
};

// Extract ERC data for exactly 5 years
const extractERCData = (product, loanAmount, productRate, mortgageTermYears, shortlistedProducts = []) => {
  if (!product || !loanAmount || loanAmount <= 0) return [];

  const p = product?._raw || product || {};
  let ercArray = shortlistedProducts[0]?.shortlisted_erc ||
    p?.erc ||
    p?.early_repayment_charges ||
    p?.shortlisted_erc ||
    p?.early_repayment ||
    product?.erc ||
    product?.early_repayment_charges ||
    product?.shortlisted_erc ||
    product?.early_repayment ||
    [];
  if (!Array.isArray(ercArray)) ercArray = [];

  const totalMonths = mortgageTermYears * 12;
  const monthlyPayment = calculateMonthlyPayment(loanAmount, productRate || 0, mortgageTermYears);
  const today = new Date();

  const results = [];
  for (let year = 1; year <= 5; year++) {
    const monthsPaid = year * 12;
    const remainingBalance = calculateRemainingBalance(loanAmount, productRate || 0, monthlyPayment, monthsPaid);

    let chargePercent = 0;
    const ercEntry = ercArray.find(e => {
      const eYear = e.year || parseInt(String(e.year_no || "").replace("Year ", "") || "0", 10);
      return eYear === year;
    });

    if (ercEntry) {
      chargePercent = ercEntry.charge_percent || ercEntry.charge || 0;
    } else {
      const fieldVariations = [
        `erc_year_${year}`,
        `year_${year}`,
        `early_repayment_year_${year}`,
        `erc_charge_year_${year}`,
        `erc_charge_${year}`,
        `charge_year_${year}`,
        `erc${year}`
      ];

      for (const field of fieldVariations) {
        if (product[field] != null && product[field] !== "") {
          const val = parseFloat(product[field]);
          if (!isNaN(val)) {
            chargePercent = val;
            break;
          }
        }
      }
    }

    const ercAmount = (remainingBalance * chargePercent) / 100;

    const untilDate = new Date(today);
    untilDate.setFullYear(today.getFullYear() + year);
    const untilStr = untilDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });

    results.push({
      loanAmount: formatCurrency(remainingBalance),
      basis: `${formatErcPercent(chargePercent)} of the amount repaid`,
      until: `Until ${untilStr}`,
      maxCharge: formatCurrency(ercAmount),
    });
  }

  return results;
};

// Extract data from product and application
export const extractIllustrationData = (product, applicationPayload, loanAmountFromApplication = null, propertyValueFromApplication = null) => {
  const sourcingInputs = applicationPayload?.meta?.sourcingInputs || {};
  const shortlistedProducts = applicationPayload?.meta?.shortlistedProducts || [];
  const p = product?._raw || product || {};
  const shortlisted0 = shortlistedProducts[0] || {};

  const isInvoiceFinance = (() => {
    const cat = String(
      product?.product_category ||
        product?.category ||
        product?.productType ||
        product?.product_type ||
        applicationPayload?.productType ||
        applicationPayload?.product_type ||
        applicationPayload?.productCategory ||
        p?.product_category ||
        p?.category ||
        ""
    )
      .trim()
      .toUpperCase();
    const purpose = String(
      applicationPayload?.loan_purpose || product?.loan_purpose || p?.loan_purpose || ""
    ).toLowerCase();
    const id = String(product?.name || product?.product_id || product?.id || p?.name || "").toUpperCase();
    return (
      cat === "IF" ||
      cat.includes("INVOICE") ||
      purpose.includes("invoice") ||
      id.startsWith("IF-")
    );
  })();

  if (isInvoiceFinance) {
    const facilityRequired = firstFinite(
      loanAmountFromApplication,
      applicationPayload?.facility_required,
      applicationPayload?.loan_amount,
      shortlisted0?.facility_required,
      shortlisted0?.facilityRequired,
      shortlisted0?.headlineAmount,
      shortlisted0?.loan_amount,
      p?.facility_required,
      product?.facility_required,
      product?.facilityRequired,
      product?.headlineAmount,
      product?.loan_amount
    );

    const netAdvance = firstFinite(
      applicationPayload?.net_advance,
      shortlisted0?.net_advance,
      shortlisted0?.netAdvance,
      p?.net_advance,
      product?.net_advance,
      product?.netAdvance
    );

    const contractTermMonths = firstFinite(
      applicationPayload?.contract_term_months,
      shortlisted0?.contract_term_months,
      shortlisted0?.termMonths,
      shortlisted0?.term_months,
      p?.contract_term_months,
      product?.contract_term_months,
      product?.termMonths,
      product?.term_months
    );

    const facilityTypeRaw = String(
      applicationPayload?.facility_type ||
        shortlisted0?.facility_type ||
        shortlisted0?.facilityType ||
        p?.facility_type ||
        product?.facility_type ||
        product?.facilityType ||
        ""
    ).trim();
    let facilityTypeDisplay = facilityTypeRaw || "—";
    if (/^confidential$/i.test(facilityTypeRaw)) facilityTypeDisplay = "Confidential";
    else if (/^disclosure$|^disclosed$/i.test(facilityTypeRaw)) facilityTypeDisplay = "Disclosed";
    else {
      const conf =
        applicationPayload?.confidential_facility ??
        shortlisted0?.confidential_facility ??
        product?.confidential_facility ??
        product?.confidential ??
        p?.confidential_facility ??
        0;
      if (conf === true || conf === 1 || conf === "1") facilityTypeDisplay = "Confidential";
      else if (
        applicationPayload?.disclosure === 1 ||
        product?.disclosure === 1 ||
        shortlisted0?.disclosure === 1
      ) {
        facilityTypeDisplay = "Disclosed";
      }
    }

    const sector =
      applicationPayload?.sector ||
      shortlisted0?.sector ||
      product?.sector ||
      p?.sector ||
      sourcingInputs?.sector ||
      "—";

    const costOverProductTerm = firstFinite(
      applicationPayload?.cost_over_product_term,
      shortlisted0?.cost_over_product_term,
      shortlisted0?.costOverProductTerm,
      shortlisted0?.total_cost,
      shortlisted0?.totalCost,
      p?.cost_over_product_term,
      product?.cost_over_product_term,
      product?.costOverProductTerm,
      product?.total_cost,
      product?.totalCost
    );

    const minServiceFee = firstFinite(
      applicationPayload?.min_service_fee,
      shortlisted0?.min_service_fee,
      shortlisted0?.minServiceFee,
      p?.min_service_fee,
      product?.min_service_fee,
      product?.minServiceFee
    );

    const ipPercent = firstFinite(
      applicationPayload?.ip_percent,
      shortlisted0?.ip_percent,
      shortlisted0?.invoicePurchasePct,
      p?.ip_percent,
      product?.ip_percent,
      product?.invoicePurchasePct
    );

    const serviceFeePercent = resolveIfServiceFeePercent(
      applicationPayload?.service_fee_percent,
      shortlisted0?.service_fee_percent,
      shortlisted0?.annualCostPct,
      p?.service_fee_percent,
      product?.service_fee_percent,
      product?.annualCostPct,
      applicationPayload?.simple_service_fee,
      shortlisted0?.simple_service_fee,
      product?.simple_service_fee,
      p?.simple_service_fee
    );

    const discountRate = firstFinite(
      applicationPayload?.discount_rate_percent,
      shortlisted0?.discount_rate_percent,
      shortlisted0?.ratePct,
      p?.discount_rate_percent,
      product?.discount_rate_percent,
      product?.ratePct
    );

    const apr = firstFinite(
      applicationPayload?.apr,
      shortlisted0?.apr,
      shortlisted0?.ratePct,
      p?.apr,
      product?.apr,
      product?.aprc,
      product?.aprc_percent
    );

    const arrangementFee = firstFinite(
      applicationPayload?.arrangement_fee,
      shortlisted0?.arrangement_fee,
      shortlisted0?.arrangementFeeAmount,
      shortlisted0?.product_fee_amount,
      p?.arrangement_fee,
      product?.arrangement_fee,
      product?.arrangementFeeAmount,
      product?.product_fee_amount,
      product?.total_fees
    );

    const abCalc = shortlisted0?.abCalc || product?.abCalc || applicationPayload?.abCalc || null;
    const additionalLoanAmount = firstFinite(
      abCalc?.abPrincipal,
      applicationPayload?.additional_borrowing_amount,
      shortlisted0?.additional_borrowing_amount,
      shortlisted0?.additionalLoanAmount,
      product?.additional_borrowing_amount,
      product?.additionalLoanAmount,
      product?.ab_principal
    );
    const hasAdditionalBorrowing =
      (Number.isFinite(additionalLoanAmount) && additionalLoanAmount > 0) || !!abCalc;

    const abInterestRate = firstFinite(
      applicationPayload?.interest_rate,
      shortlisted0?.interest_rate,
      product?.interest_rate,
      p?.interest_rate,
      abCalc?.interestRatePct,
      applicationPayload?.additional_borrowing_interest_rate,
      shortlisted0?.additional_borrowing_interest_rate,
      product?.additional_borrowing_interest_rate
    );
    const abFirst12Interest = firstFinite(
      abCalc?.first12MonthsInterest,
      applicationPayload?.first_12_month_interest,
      shortlisted0?.first_12_month_interest,
      product?.first_12_month_interest
    );
    const abArrangementFee = firstFinite(
      applicationPayload?.ab_arrangement_fee,
      abCalc?.arrangementFee,
      product?.ab_arrangement_fee,
      shortlisted0?.ab_arrangement_fee
    );
    const abTermMonths = firstFinite(
      abCalc?.termMonths,
      product?.ab_term_months,
      shortlisted0?.ab_term_months,
      contractTermMonths
    );
    const abMonthlyRepayment = firstFinite(
      abCalc?.monthlyRepayment,
      applicationPayload?.monthly_repayments,
      applicationPayload?.monthly_payment,
      shortlisted0?.monthly_repayments,
      shortlisted0?.monthlyCost,
      product?.monthly_repayments,
      product?.monthlyCost
    );
    const abTotalRepayable = firstFinite(
      abCalc?.totalRepayable,
      applicationPayload?.total_repayable,
      shortlisted0?.total_repayable,
      product?.total_repayable
    );
    const abCostOverTerm = firstFinite(
      applicationPayload?.ab_cost_over_product_term,
      applicationPayload?.ab_cost_over_term,
      abCalc?.costOverTerm,
      product?.ab_cost_over_product_term,
      product?.ab_cost_over_term,
      shortlisted0?.ab_cost_over_product_term,
      shortlisted0?.ab_cost_over_term
    );

    const productId =
      product?.name ||
      product?.product_id ||
      product?.productId ||
      product?.mortgage_product ||
      product?.id ||
      shortlisted0?.mortgage_product ||
      shortlisted0?.product_id ||
      shortlisted0?.productId ||
      applicationPayload?.product ||
      applicationPayload?.product_id ||
      applicationPayload?.productId ||
      "—";

    return {
      isInvoiceFinance: true,
      productId,
      productName:
        product?.product_name ||
        product?.productName ||
        product?.displayTitle ||
        product?.name ||
        "Invoice Finance",
      sector: sector || "—",
      facilityRequiredDisplay: formatCurrency(facilityRequired),
      contractTermDisplay:
        Number.isFinite(contractTermMonths) && contractTermMonths > 0
          ? `${Math.round(contractTermMonths)} months`
          : "—",
      netAdvanceDisplay: formatCurrency(netAdvance),
      facilityTypeDisplay,
      costOverProductTermDisplay: formatCurrency(costOverProductTerm),
      minServiceFeeDisplay: formatCurrency(minServiceFee),
      ipDisplay:
        Number.isFinite(ipPercent) ? `${Math.round(ipPercent)}%` : "—",
      serviceFeeDisplay: Number.isFinite(serviceFeePercent)
        ? formatIfServiceFeePercent(serviceFeePercent)
        : "—",
      discountRateDisplay: Number.isFinite(discountRate) ? formatPercent(discountRate) : "—",
      aprDisplay: Number.isFinite(apr) ? formatPercent(apr) : "—",
      arrangementFeeDisplay: formatCurrency(arrangementFee),
      hasAdditionalBorrowing,
      additionalBorrowing: {
        loanAmountDisplay: formatCurrency(additionalLoanAmount),
        interestRateDisplay: Number.isFinite(abInterestRate) ? formatPercent(abInterestRate) : "—",
        first12MonthsInterestDisplay: formatCurrency(abFirst12Interest),
        arrangementFeeDisplay: formatCurrency(abArrangementFee),
        contractTermDisplay:
          Number.isFinite(abTermMonths) && abTermMonths > 0
            ? `${Math.round(abTermMonths)} months`
            : "—",
        monthlyRepaymentDisplay: formatCurrency(abMonthlyRepayment),
        totalRepayableDisplay: formatCurrency(abTotalRepayable),
        costOverTermDisplay: formatCurrency(abCostOverTerm),
      },
      // Keep BTL-compatible keys empty so shared callers don't break
      loanAmountDisplay: formatCurrency(facilityRequired),
      ercData: [],
    };
  }

  const firstStringCandidate = (...values) => {
    for (const value of values) {
      if (value == null) continue;

      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed && trimmed.toLowerCase() !== "[object object]") return trimmed;
        continue;
      }

      if (Array.isArray(value)) {
        const joined = value
          .map((v) => (typeof v === "string" ? v.trim() : String(v ?? "").trim()))
          .filter(Boolean)
          .join(", ");
        if (joined && joined.toLowerCase() !== "[object object]") return joined;
        continue;
      }

      if (typeof value === "object") {
        // Common shape from backend/API adapters: { label: "Fixed", value: "fixed" }
        const labelLike = value?.label || value?.name || value?.value || value?.type || value?.product_type;
        if (typeof labelLike === "string" && labelLike.trim()) {
          const trimmed = labelLike.trim();
          if (trimmed.toLowerCase() !== "[object object]") return trimmed;
        }
      }
    }
    return "";
  };

  const loanAmount = loanAmountFromApplication ||
    shortlistedProducts[0]?.loan_amount ||
    shortlistedProducts[0]?.mortgageAmount ||
    shortlistedProducts[0]?.mortgage_amount ||
    sourcingInputs?.mortgageAmount ||
    sourcingInputs?.loanAmount ||
    applicationPayload?.loanAmount ||
    applicationPayload?.mortgageAmount ||
    p?.loan_amount ||
    p?.mortgage_amount ||
    p?.amount ||
    product?.loan_amount ||
    product?.mortgage_amount ||
    0;

  const propertyValue = propertyValueFromApplication ||
    shortlistedProducts[0]?.propertyValue ||
    shortlistedProducts[0]?.property_value ||
    sourcingInputs?.propertyValue ||
    sourcingInputs?.property_value ||
    applicationPayload?.propertyValue ||
    applicationPayload?.property_value ||
    p?.property_value ||
    p?.value ||
    product?.property_value ||
    0;

  const rawRepaymentType =
    applicationPayload?.repayment_method ||
    applicationPayload?.repayment_type ||
    applicationPayload?.repaymentType ||
    applicationPayload?.formData?.repayment_type ||
    applicationPayload?.formData?.repaymentType ||
    applicationPayload?.fullPayload?.repayment_type ||
    applicationPayload?.fullPayload?.repaymentType ||
    applicationPayload?.btl?.solo?.repayment_type ||
    applicationPayload?.btl?.solo?.repaymentType ||
    applicationPayload?.btl?.limited?.repayment_type ||
    applicationPayload?.btl?.partnership?.repayment_type ||
    applicationPayload?.btl?.pension?.repayment_type ||
    applicationPayload?.meta?.sourcingInputs?.repaymentType ||
    applicationPayload?.meta?.sourcingInputs?.repayment_type ||
    applicationPayload?.meta?.filters?.repaymentType ||
    sourcingInputs?.repaymentType ||
    sourcingInputs?.repayment_type ||
    applicationPayload?.mortgage_type ||
    shortlistedProducts[0]?.repaymentType ||
    shortlistedProducts[0]?.repayment_type ||
    p?.repayment_type ||
    p?.repayment_method ||
    product?.repayment_type ||
    product?.repayment_method ||
    "Repayment";

  // Backend returns e.g. "repayment_type": "Repayment" or "Interest only" — use explicit checks so "Repayment" is never shown as Interest only
  const rawStr = String(rawRepaymentType ?? "").trim().toLowerCase();
  const isInterestOnly =
    rawStr === "interest only" ||
    rawStr.startsWith("interest only") ||
    rawStr === "io";
  // Explicitly treat "repayment" as Repayment (do not use .includes("interest") which could misclassify)

  const repaymentTypeDisplay = isInterestOnly ? "Interest Only" : "Repayment";
  const repaymentMethodDisplay = repaymentTypeDisplay;

  const arrangementFeeAmount = resolveArrangementFeeAmount(product, loanAmount);

  // IMPORTANT: payments are based on loan + arrangement fee (fee capitalised) for BOTH repayment + interest-only
  const paymentPrincipal = safeNum(loanAmount) + safeNum(arrangementFeeAmount);

  const arrangementFeePercent = p?.product_fee_percent || p?.arrangement_fee_percent || product?.product_fee_percent || null;

  let arrangementFeeDisplay = formatCurrency(arrangementFeeAmount);
  if (arrangementFeePercent) {
    arrangementFeeDisplay = `${arrangementFeePercent}% or ${arrangementFeeDisplay}`;
  }

  const productTermYears = p?.product_termsyears || p?.product_term_years || p?.product_term || product?.product_termsyears || null;
  const productTermRaw = String(productTermYears ?? "").trim();
  const isLifetimeProduct = productTermRaw.toLowerCase() === "lifetime";
  const numericProductTermYears = Number.parseFloat(productTermRaw);

  const rateType = firstStringCandidate(
    shortlistedProducts[0]?.product_type_list,
    shortlistedProducts[0]?.rate_type,
    shortlistedProducts[0]?.rateType ||
    shortlistedProducts[0]?.product_rate_type ||
    shortlistedProducts[0]?.productRateType ||
    p?.product_type_list,
    p?.product_rate_type ||
    p?.rate_type ||
    p?.product_type ||
    product?.product_type_list,
    product?.product_rate_type ||
    product?.rate_type ||
    product?.product_type ||
    product?.rateType ||
    shortlistedProducts[0]?.productType ||
    shortlistedProducts[0]?.product_type ||
    null
  );

  let productTypeDisplay = "Variable";
  if (rateType) {
    const rt = firstStringCandidate(rateType);
    if (rt.toLowerCase().includes("fixed")) productTypeDisplay = "Fixed";
    else if (rt.toLowerCase().includes("variable")) productTypeDisplay = "Variable";
    else if (rt.toLowerCase().includes("discounted")) productTypeDisplay = "Discounted";
    else if (rt.toLowerCase().includes("tracker")) productTypeDisplay = "Tracker";
    else {
      productTypeDisplay = rt.charAt(0).toUpperCase() + rt.slice(1).toLowerCase();
    }
  } else if (productTermYears && parseFloat(productTermYears) > 0) {
    productTypeDisplay = "Fixed";
  }

  const mortgageTermYears = p?.mortgage_term_years ||
    p?.mortgage_term ||
    product?.mortgage_term_years ||
    sourcingInputs?.mortgageTerm ||
    25;
  const mortgageTermMonths = mortgageTermYears * 12;

  const ltv = p?.ity_percent || p?.ltv || p?.ltv_percent || product?.ltv || null;
  const ltvDisplay = ltv ? `${ltv}%` : "—";

  const valuation = p?.free_valuation || product?.free_valuation ? "Free" : (p?.valuation_fee || product?.valuation_fee ? formatCurrency(p?.valuation_fee || product?.valuation_fee) : "TBC");
  const legals = p?.free_legals || product?.free_legals ? "Free" : (p?.legal_fee || product?.legal_fee ? formatCurrency(p?.legal_fee || product?.legal_fee) : "TBC");
  const overpayment = p?.overpayents || p?.overpayments || product?.overpayments ? (p?.overpayment_percent || product?.overpayment_percent ? `Up to ${p?.overpayment_percent || product?.overpayment_percent}% per year` : "Yes") : "No";
  const portability = p?.portable || product?.portable ? "Yes" : "No";
  const cashback = p?.cashback || product?.cashback ? formatCurrency(p?.cashback || product?.cashback) : "Not specified";
  const underpayments = p?.underpayments || product?.underpayments ? "Yes" : "Not specified";

  const productRate = p?.product_rate_percent || p?.product_rate || p?.rate || product?._rateNum || 0;
  const svrRate = p?.svr_margin_percent || p?.svr || null;

  const productTermMonths = isLifetimeProduct
    ? mortgageTermMonths
    : Number.isFinite(numericProductTermYears) && numericProductTermYears > 0
      ? Math.round(numericProductTermYears * 12)
      : 0;
  const remainingTermMonths = isLifetimeProduct ? 0 : Math.max(0, Math.round(mortgageTermMonths - productTermMonths));

  const fixedMonthlyPaymentNumeric = productTermMonths > 0
    ? (isInterestOnly
        ? calculateInterestOnlyMonthlyPayment(paymentPrincipal, productRate)
        : calculateMonthlyPayment(paymentPrincipal, productRate, mortgageTermYears))
    : null;

  const balanceAfterFixed = (!isInterestOnly && fixedMonthlyPaymentNumeric && productTermMonths > 0)
    ? calculateRemainingBalance(paymentPrincipal, productRate || 0, fixedMonthlyPaymentNumeric, productTermMonths)
    : paymentPrincipal;

  const variableMonthlyPayment = (svrRate && remainingTermMonths > 0)
    ? (isInterestOnly
        ? calculateInterestOnlyMonthlyPayment(paymentPrincipal, svrRate)
        : calculateMonthlyPaymentWithMonths(balanceAfterFixed, svrRate, remainingTermMonths))
    : null;

  const fixedMonthlyPayment = fixedMonthlyPaymentNumeric;

  // Amount of Each Instalment: prefer monthly_payment from application/shortlisted product (e.g. £917.93)
  const monthlyPaymentFromPayload = firstFinite(
    applicationPayload?.monthly_payment,
    applicationPayload?.monthly_repayments,
    shortlistedProducts[0]?.monthly_payment,
    shortlistedProducts[0]?.monthlyPayment,
    shortlistedProducts[0]?.monthly_repayments,
    shortlistedProducts[0]?.repayment,
    applicationPayload?.meta?.shortlistedProducts?.[0]?.monthly_payment,
    applicationPayload?.meta?.shortlistedProducts?.[0]?.monthly_repayments,
    p?.monthly_payment,
    product?.monthly_payment,
    product?.monthlyPayment,
    product?.repayment
  );
  const instalmentAmountForDisplay = Number.isFinite(monthlyPaymentFromPayload) ? monthlyPaymentFromPayload : fixedMonthlyPayment;

  // Total amount to be paid = Loan amount + Cost over product term (when both are available from application)
  const costOverProductTerm = firstFinite(
    applicationPayload?.cost_over_product_term,
    shortlisted0?.cost_over_product_term,
    shortlisted0?.total_cost,
    shortlisted0?.totalCost,
    applicationPayload?.meta?.shortlistedProducts?.[0]?.cost_over_product_term,
    applicationPayload?.meta?.shortlistedProducts?.[0]?.total_cost,
    p?.cost_over_product_term,
    p?.total_cost,
    product?.cost_over_product_term,
    product?.total_cost,
    product?.totalCost
  );
  // Prefer application loan amount for this sum so total matches application context (not product default)
  const loanAmountForTotal = firstFinite(
    loanAmountFromApplication,
    applicationPayload?.loan_amount,
    applicationPayload?.mortgageAmount,
    applicationPayload?.loanAmount,
    shortlistedProducts[0]?.loan_amount,
    shortlistedProducts[0]?.mortgageAmount,
    loanAmount
  );
  const totalAmountPaid = Number.isFinite(costOverProductTerm) && costOverProductTerm >= 0 && Number.isFinite(loanAmountForTotal) && loanAmountForTotal > 0
    ? loanAmountForTotal + costOverProductTerm
    : (fixedMonthlyPayment && variableMonthlyPayment
      ? (fixedMonthlyPayment * productTermMonths) + (variableMonthlyPayment * remainingTermMonths)
      : fixedMonthlyPayment
        ? fixedMonthlyPayment * mortgageTermMonths
        : null);

  return {
    productId: product?.name || product?.product_id || product?.id || "—",
    productName: product?.product_name || product?.productName || product?.name || "—",
    loanAmountDisplay: formatCurrency(loanAmount),
    mortgageTerm: mortgageTermYears,
    repaymentMethodDisplay,
    productTypeDisplay,
    arrangementFeeDisplay,
    totalAmountPaidDisplay: formatCurrency(totalAmountPaid),
    propertyValueDisplay: formatCurrency(propertyValue),
    ltvDisplay,
    aprcDisplay: product?.aprc_percent || product?.aprc ? formatPercent(product.aprc_percent || product.aprc) : "—",
    productRate,
    productRateDisplay: formatPercent(productRate),
    svrDisplay: svrRate ? formatPercent(svrRate) : "—",
    productTermYears,
    isLifetimeProduct,
    productTermMonths,
    remainingTermMonths,
    fixedMonthlyPaymentDisplay: formatCurrency(instalmentAmountForDisplay),
    variableMonthlyPaymentDisplay: formatCurrency(variableMonthlyPayment),
    ercData: extractERCData(product, loanAmount, productRate, mortgageTermYears, shortlistedProducts),
    valuation,
    legals,
    overpayment,
    portability,
    cashback,
    underpayments,
    numberOfPayments: mortgageTermMonths,
  };
};

// ============================================================================
// MAIN PDF GENERATION FUNCTION
// ============================================================================

const drawIllustrationHeader = (doc, { fontFamily, darkBlue, teal, margin, pageWidth, yPos, logoBase64, titleLines }) => {
  const gap = 4;
  const logoH = 20;
  const logoW = 52;
  const headerH = Math.max(24, logoH + 4);
  const headerCenterY = yPos + headerH / 2;
  const titleX = margin;
  doc.setFont(fontFamily, "bold");
  doc.setFontSize(18);
  doc.setTextColor(...darkBlue);
  const lines = titleLines || ["MORTGAGE", "ILLUSTRATION"];
  doc.text(lines[0], titleX, headerCenterY - 3);
  doc.text(lines[1] || "", titleX, headerCenterY + 3);

  const logoX = pageWidth - margin - logoW;
  const logoY = yPos + (headerH - logoH) / 2 - 3;
  if (logoBase64) {
    try {
      addPdfLogoImage(doc, logoBase64, logoX, logoY, logoW, logoH);
    } catch (err) {
      console.error("Logo error:", err);
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(11);
      doc.setTextColor(...teal);
      doc.text("Dynesourcer", logoX, yPos + 8);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text("SOURCING SIMPLIFIED", logoX, yPos + 12);
    }
  } else {
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(11);
    doc.setTextColor(...teal);
    doc.text("Dynesourcer", logoX, yPos + 8);
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text("SOURCING SIMPLIFIED", logoX, yPos + 12);
  }
  return yPos + headerH + gap;
};

const drawTwoColumnLabelRows = (
  doc,
  {
    fontFamily,
    darkBlue,
    teal,
    leftX,
    rightX,
    startY,
    leftRows,
    rightRows,
    colW,
    pad,
    labelW = 42,
    rowH = 4.8,
    valueInset = 12,
  }
) => {
  const leftColRight = leftX + colW - pad - valueInset;
  let leftY = startY;
  let rightY = startY;
  const rows = Math.max(leftRows.length, rightRows.length);
  for (let i = 0; i < rows; i++) {
    if (leftRows[i]) {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(8);
      doc.setTextColor(...darkBlue);
      doc.text(`${leftRows[i][0]}:`, leftX, leftY + 3);
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(...teal);
      const v = doc.splitTextToSize(String(leftRows[i][1] ?? "—"), colW - labelW - 4);
      const vArr = Array.isArray(v) ? v : [v];
      let vy = leftY + 3;
      vArr.forEach((line) => {
        doc.text(line, leftColRight - doc.getTextWidth(line), vy);
        vy += 4;
      });
      leftY += rowH + Math.max(0, (vArr.length - 1) * 4);
    }
    if (rightRows[i]) {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(8);
      doc.setTextColor(...darkBlue);
      doc.text(`${rightRows[i][0]}:`, rightX, rightY + 3);
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(...teal);
      const v = doc.splitTextToSize(String(rightRows[i][1] ?? "—"), colW - labelW - 4);
      doc.text(v, rightX + labelW, rightY + 3);
      rightY += rowH;
    }
  }
  return Math.max(leftY, rightY);
};

export const generateInvoiceFinanceIllustrationPDF = (
  data,
  clientDetails = {},
  logoBase64 = null,
  fontFiles = null
) => {
  try {
    const doc = createIllustrationPdf();
    const fontNormal = fontFiles?.normal && isValidBase64ForVFS(fontFiles.normal) ? fontFiles.normal : null;
    const fontBold = fontFiles?.bold && isValidBase64ForVFS(fontFiles.bold) ? fontFiles.bold : null;
    if (fontNormal) {
      try {
        doc.addFileToVFS("BrandonGrotesque-Regular.ttf", fontNormal);
        doc.addFont("BrandonGrotesque-Regular.ttf", ILLUSTRATION_FONT, "normal");
      } catch (e) {
        console.warn("Brandon Grotesque normal font registration failed:", e);
      }
    }
    if (fontBold) {
      try {
        doc.addFileToVFS("BrandonGrotesque-Bold.ttf", fontBold);
        doc.addFont("BrandonGrotesque-Bold.ttf", ILLUSTRATION_FONT, "bold");
      } catch (e) {
        console.warn("Brandon Grotesque bold font registration failed:", e);
      }
    }
    const fontFamily = ILLUSTRATION_FONT_FALLBACK;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const gap = 4;
    let yPos = 6;

    const darkBlue = [52, 73, 94];
    const teal = [79, 209, 197];
    const yellow = [255, 242, 0];
    const cardBg = [255, 255, 255];
    const borderGrey = [226, 232, 240];
    const fullWidth = pageWidth - margin * 2;

    const drawCard = (x, y, w, h) => {
      doc.setDrawColor(...borderGrey);
      doc.setLineWidth(0.5);
      doc.setFillColor(...cardBg);
      doc.roundedRect(x, y, w, h, 3, 3, "FD");
    };

    yPos = drawIllustrationHeader(doc, {
      fontFamily,
      darkBlue,
      teal,
      margin,
      pageWidth,
      yPos,
      logoBase64,
      titleLines: ["INVOICE FINANCE", "ILLUSTRATION"],
    });

    const colW = (fullWidth - gap) / 2;
    const pad = 3;
    const lineInset = 3;
    const leftX = margin + pad;
    const rightX = margin + colW + gap + pad;

    // ---- Client Details | Intermediary (no security address) ----
    const ownershipType = (clientDetails.ownershipType || clientDetails.ownership_type || "").trim();
    const companyName = (clientDetails.companyName || clientDetails.company_name || "").trim();
    const isLimited = /limited|ltd|pvt\s*ltd|llp/i.test(ownershipType);
    const toTitleCase = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    let borrowerTypeLabel = "";
    if (ownershipType) {
      if (isLimited && companyName) borrowerTypeLabel = `Ltd Co. / ${companyName}`;
      else if (isLimited) borrowerTypeLabel = "Ltd Co.";
      else if (/sole\s*proprietorship/i.test(ownershipType)) borrowerTypeLabel = "Solo Trader";
      else if (/partnership/i.test(ownershipType)) borrowerTypeLabel = "Partnership";
      else if (/pension|trust/i.test(ownershipType)) borrowerTypeLabel = "Pension";
      else borrowerTypeLabel = toTitleCase(ownershipType);
    }

    // Intermediary copy needs enough height + bottom inset so it doesn't sit on the card border
    const interLineH = 3.6;
    const interBottomPad = 5;
    const interStartOffset = 12;
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(7.5);
    const interParagraph1 =
      "Dynesourcer Ltd. is an Introducing Appointed Representative (IAR) of Dynamic Commercial Finance Ltd and does not recommend a specific product after assessing your needs and circumstances.";
    const interLinesAfterBreak = [
      "Dynamic Commercial Finance Ltd",
      "FCA: 946483 | Dynamic Commercial Finance is a broker not a lender.",
      "Top Barn One, Lamport Manor",
      "Lamport, Northamptonshire NN6 9HF",
      "Tel: 0203 978 6758 | admin@dynamiccf.co.uk",
    ];
    const interParaLines = doc.splitTextToSize(interParagraph1, colW - pad * 2);
    let interContentH = interStartOffset + interParaLines.length * interLineH + 3;
    interLinesAfterBreak.forEach((line) => {
      interContentH += doc.splitTextToSize(line, colW - pad * 2).length * interLineH;
    });
    interContentH += interBottomPad;

    const clientBlockH = (borrowerTypeLabel ? 17 : 14) + 3 * 4 + 6;
    const card1H = Math.max(48, Math.ceil(Math.max(clientBlockH, interContentH)));
    drawCard(margin, yPos, fullWidth, card1H);
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + gap / 2, yPos + lineInset, margin + colW + gap / 2, yPos + card1H - lineInset);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(11);
    doc.setTextColor(...darkBlue);
    doc.text("Client Details", leftX, yPos + 5);
    doc.text("Intermediary Details", rightX, yPos + 5);

    const clientRows = [
      ["Client Name", clientDetails.name || ""],
      ["Telephone", clientDetails.telephone || ""],
      ["Email", clientDetails.email || ""],
    ];
    let ty = yPos + 14;
    if (borrowerTypeLabel) {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(10);
      doc.setTextColor(...teal);
      doc.text(borrowerTypeLabel, leftX, yPos + 11);
      ty = yPos + 17;
    }
    clientRows.forEach(([label, value]) => {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(8);
      doc.setTextColor(...darkBlue);
      const labelStr = `${label}: `;
      doc.text(labelStr, leftX, ty);
      const labelWidth = doc.getTextWidth(labelStr);
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(...teal);
      const maxW = colW - pad * 2 - labelWidth;
      const lines = value ? doc.splitTextToSize(String(value), maxW) : [""];
      lines.forEach((line) => {
        doc.text(line, leftX + labelWidth, ty);
        ty += 4;
      });
    });

    // Draw intermediary slightly higher with tighter leading + bottom inset inside the card
    ty = yPos + interStartOffset;
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    interParaLines.forEach((s) => {
      doc.text(s, rightX, ty);
      ty += interLineH;
    });
    ty += 3;
    interLinesAfterBreak.forEach((line) => {
      doc.splitTextToSize(line, colW - pad * 2).forEach((s) => {
        doc.text(s, rightX, ty);
        ty += interLineH;
      });
    });
    yPos += card1H + gap;

    // ---- Main Features | Interest Rates ----
    const mainFeatures = [
      ["Product ID", data.productId || "—"],
      ["Sector", data.sector || "—"],
      ["Facility Required", data.facilityRequiredDisplay || "—"],
      ["Term Contract", data.contractTermDisplay || "—"],
      ["Net Advance", data.netAdvanceDisplay || "—"],
      ["Facility Type", data.facilityTypeDisplay || "—"],
      ["Cost over product term", data.costOverProductTermDisplay || "—"],
      ["Min. Service Fee", data.minServiceFeeDisplay || "—"],
    ];
    const interestRates = [
      ["IP", data.ipDisplay || "—"],
      ["Service Fee", data.serviceFeeDisplay || "—"],
      ["Discount rate", data.discountRateDisplay || "—"],
      ["APR", data.aprDisplay || "—"],
      ["Arrangement fee", data.arrangementFeeDisplay || "—"],
    ];

    const card2Rows = Math.max(mainFeatures.length, interestRates.length);
    const card2RowH = 4.8;
    const card2H = 8 + card2Rows * card2RowH + 6;
    drawCard(margin, yPos, fullWidth, card2H);
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + gap / 2, yPos + lineInset, margin + colW + gap / 2, yPos + card2H - lineInset);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Main Features of the Facility", margin + pad, yPos + 5);
    doc.text("Interest Rates & Other Costs", margin + colW + gap + pad, yPos + 5);

    drawTwoColumnLabelRows(doc, {
      fontFamily,
      darkBlue,
      teal,
      leftX,
      rightX,
      startY: yPos + 8,
      leftRows: mainFeatures,
      rightRows: interestRates,
      colW,
      pad,
      rowH: card2RowH,
    });
    yPos += card2H + gap;

    // ---- Additional Borrowing (replaces instalment / frequency section) ----
    const ab = data.additionalBorrowing || {};
    const abRows = data.hasAdditionalBorrowing
      ? [
          ["Loan amount", ab.loanAmountDisplay || "—"],
          ["Interest rate", ab.interestRateDisplay || "—"],
          ["First 12 months interest", ab.first12MonthsInterestDisplay || "—"],
          ["Arrangement fee", ab.arrangementFeeDisplay || "—"],
          ["Contract term", ab.contractTermDisplay || "—"],
          ["Monthly repayment", ab.monthlyRepaymentDisplay || "—"],
          ["Total repayable", ab.totalRepayableDisplay || "—"],
          ["Cost over product term", ab.costOverTermDisplay || "—"],
        ]
      : [];
    const abRowH = 5;
    const card3H = data.hasAdditionalBorrowing
      ? 10 + abRows.length * abRowH + 4
      : 28;
    drawCard(margin, yPos, fullWidth, card3H);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Additional Borrowing", margin + pad, yPos + 6);
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(8);
    doc.setTextColor(...teal);
    doc.text("Capital & Interest", margin + pad + 48, yPos + 6);

    if (data.hasAdditionalBorrowing) {
      const mid = Math.ceil(abRows.length / 2);
      const leftAb = abRows.slice(0, mid);
      const rightAb = abRows.slice(mid);
      drawTwoColumnLabelRows(doc, {
        fontFamily,
        darkBlue,
        teal,
        leftX,
        rightX,
        startY: yPos + 10,
        leftRows: leftAb,
        rightRows: rightAb,
        colW,
        pad,
        rowH: abRowH,
        labelW: 52,
      });
    } else {
      doc.setFont(fontFamily, "normal");
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        "Not available for this product. Additional borrowing is only shown for Capital Repayment products.",
        margin + pad,
        yPos + 16
      );
    }
    yPos += card3H + 4;

    // ---- Footer contact cards + disclaimer ----
    // Keep a consistent bottom margin after the red disclaimer (avoid a large empty gap).
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerHeight = 22;
    const gapBetweenCardsAndDisclaimer = 6;
    const bottomMarginAfterDisclaimer = 14;
    const disclaimerLineH = 4;

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(8);
    const disclaimerText =
      "This illustration is for guidance only and subject to application, underwriting and credit approval. Facility terms, fees and availability may change.";
    const disclaimerLines = doc.splitTextToSize(disclaimerText, fullWidth);
    const disclaimerBlockH = Math.max(disclaimerLines.length, 1) * disclaimerLineH;

    let footerCardsY = yPos;
    let disclaimerY = footerCardsY + footerHeight + gapBetweenCardsAndDisclaimer;
    const spaceAfterDisclaimer = pageHeight - (disclaimerY + disclaimerBlockH);
    if (spaceAfterDisclaimer > bottomMarginAfterDisclaimer) {
      const shift = spaceAfterDisclaimer - bottomMarginAfterDisclaimer;
      footerCardsY += shift;
      disclaimerY += shift;
    }

    doc.setFillColor(...darkBlue);
    doc.setDrawColor(...darkBlue);
    doc.roundedRect(margin, footerCardsY, colW, footerHeight, 4, 4, "FD");
    const leftBlockCenterX = margin + colW / 2;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text("Need Help?", leftBlockCenterX, footerCardsY + 8, { align: "center" });
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(9);
    doc.text("Speak to our adviser for next steps.", leftBlockCenterX, footerCardsY + 14, {
      align: "center",
    });

    const rightBlockX = margin + colW + gap;
    const contactCardCenterX = rightBlockX + colW / 2;
    doc.setFillColor(...yellow);
    doc.setDrawColor(...yellow);
    doc.roundedRect(rightBlockX, footerCardsY, colW, footerHeight, 4, 4, "FD");
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(12);
    doc.setTextColor(...darkBlue);
    doc.text("CONTACT DETAILS", contactCardCenterX, footerCardsY + 6, { align: "center" });
    doc.setFontSize(9);
    doc.text("Josh Mander", contactCardCenterX, footerCardsY + 12, { align: "center" });
    doc.text("admin@dynamicCF.co.uk", contactCardCenterX, footerCardsY + 17, { align: "center" });

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(8);
    doc.setTextColor(255, 0, 0);
    disclaimerLines.forEach((line) => {
      doc.text(line, margin, disclaimerY);
      disclaimerY += disclaimerLineH;
    });

    const safeName = String(clientDetails.name || "Client").replace(/[^\w\- ]+/g, "").trim() || "Client";
    doc.save(`Invoice_Finance_Illustration_${safeName}.pdf`);
  } catch (error) {
    console.error("IF PDF generation error:", error);
  }
};

// Main PDF Generation - Updated ERC table to match reference image
// fontFiles: optional { normal: base64, bold: base64 } from jsPDF font converter to use Petunia Sans
export const generateClientIllustrationPDF = (data, clientDetails = {}, logoBase64 = null, fontFiles = null) => {
  if (data?.isInvoiceFinance) {
    return generateInvoiceFinanceIllustrationPDF(data, clientDetails, logoBase64, fontFiles);
  }
  try {
    const doc = createIllustrationPdf();
    const fontNormal = fontFiles?.normal && isValidBase64ForVFS(fontFiles.normal) ? fontFiles.normal : null;
    const fontBold = fontFiles?.bold && isValidBase64ForVFS(fontFiles.bold) ? fontFiles.bold : null;
    if (fontNormal) {
      try {
        doc.addFileToVFS("BrandonGrotesque-Regular.ttf", fontNormal);
        doc.addFont("BrandonGrotesque-Regular.ttf", ILLUSTRATION_FONT, "normal");
      } catch (e) {
        console.warn("Brandon Grotesque normal font registration failed:", e);
      }
    }
    if (fontBold) {
      try {
        doc.addFileToVFS("BrandonGrotesque-Bold.ttf", fontBold);
        doc.addFont("BrandonGrotesque-Bold.ttf", ILLUSTRATION_FONT, "bold");
      } catch (e) {
        console.warn("Brandon Grotesque bold font registration failed:", e);
      }
    }
    const fontFamily = ILLUSTRATION_FONT_FALLBACK;

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const gap = 4;
    let yPos = 6;

    const parsedProductTermMonths = Number(data.productTermMonths);
    const parsedProductTermYears = Number(data.productTermYears);
    const parsedMortgageTermYears = Number(data.mortgageTerm);
    const fallbackMortgageMonths = Number.isFinite(parsedMortgageTermYears) && parsedMortgageTermYears > 0
      ? Math.round(parsedMortgageTermYears * 12)
      : 300;
    const isLifetimeProduct = String(data.productTermYears ?? "").trim().toLowerCase() === "lifetime";
    const resolvedProductTermMonths = isLifetimeProduct
      ? fallbackMortgageMonths
      : Number.isFinite(parsedProductTermMonths)
        ? Math.round(parsedProductTermMonths)
        : Number.isFinite(parsedProductTermYears) && parsedProductTermYears > 0
          ? Math.round(parsedProductTermYears * 12)
          : 24;
    const safeData = {
      productId: data.productId || "—",
      productName: data.productName || "—",
      loanAmountDisplay: data.loanAmountDisplay || "—",
      mortgageTerm: data.mortgageTerm || 25,
      repaymentMethodDisplay: data.repaymentMethodDisplay || "Repayment",
      productTypeDisplay: data.productTypeDisplay || "Fixed",
      arrangementFeeDisplay: data.arrangementFeeDisplay || "—",
      totalAmountPaidDisplay: data.totalAmountPaidDisplay || "—",
      propertyValueDisplay: data.propertyValueDisplay || "—",
      ltvDisplay: data.ltvDisplay || "—",
      aprcDisplay: data.aprcDisplay || "—",
      productRateDisplay: data.productRateDisplay || "—",
      svrDisplay: data.svrDisplay || "—",
      productTermYears: data.productTermYears || 2,
      isLifetimeProduct,
      productTermMonths: resolvedProductTermMonths,
      fixedMonthlyPaymentDisplay: data.fixedMonthlyPaymentDisplay || "—",
      variableMonthlyPaymentDisplay: data.variableMonthlyPaymentDisplay || "—",
      remainingTermMonths: isLifetimeProduct ? 0 : (data.remainingTermMonths || 0),
      numberOfPayments: data.numberOfPayments || 300,
      ercData: data.ercData || [],
      valuation: data.valuation || "TBC",
      legals: data.legals || "TBC",
      overpayment: data.overpayment || "No",
      portability: data.portability || "No",
      cashback: data.cashback || "Not specified",
      underpayments: data.underpayments || "Not specified"
    };

    // 1. COLORS
    const darkBlue = [52, 73, 94];   // Dark header/footer color
    const teal = [79, 209, 197];     // Bright teal for values
    const yellow = [255, 242, 0];           // Contact Details card background
    const cardBg = [255, 255, 255];  // Cards are white
    const borderGrey = [226, 232, 240]; // Light border
    const tableHeaderBg = [230, 255, 250]; // Light teal for table header
    const tableRowAlt = [230, 248, 248];

    const fullWidth = pageWidth - margin * 2;

    // 2. DRAW CARD (Rounded corners)
    const drawCard = (x, y, w, h) => {
      doc.setDrawColor(...borderGrey);
      doc.setLineWidth(0.5);
      doc.setFillColor(...cardBg);
      doc.roundedRect(x, y, w, h, 3, 3, "FD");
    };

    const drawTealRect = (x, y, w, h) => {
      doc.setFillColor(230, 255, 250);
      doc.setDrawColor(...borderGrey);
      doc.roundedRect(x, y, w, h, 2, 2, "FD");
    };

    // ---- HEADER: stacked title left, logo right (vertically centered on same axis) ----
    const logoH = 20;
    const logoW = 52;
    const headerH = Math.max(24, logoH + 4);
    const headerCenterY = yPos + headerH / 2;
    const titleX = margin;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(18);
    doc.setTextColor(...darkBlue);
    // Center the two-line title in the header so it aligns with logo on x-axis
    doc.text("MORTGAGE", titleX, headerCenterY - 3);
    doc.text("ILLUSTRATION", titleX, headerCenterY + 3);

    const logoX = pageWidth - margin - logoW;
    const logoY = yPos + (headerH - logoH) / 2 - 3; // Slightly up to align with title
    if (logoBase64) {
      try {
        addPdfLogoImage(doc, logoBase64, logoX, logoY, logoW, logoH);
      } catch (err) {
        console.error("Logo error:", err);
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(11);
        doc.setTextColor(...teal);
        doc.text("Dynesourcer", logoX, yPos + 8);
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text("SOURCING SIMPLIFIED", logoX, yPos + 12);
      }
    } else {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(11);
      doc.setTextColor(...teal);
      doc.text("Dynesourcer", logoX, yPos + 8);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text("SOURCING SIMPLIFIED", logoX, yPos + 12);
    }
    yPos += headerH + gap;

    // ---- CARD 1: Client Details | Intermediary Details (two columns) ----
    const colW = (fullWidth - gap) / 2;
    const card1H = 58;
    drawCard(margin, yPos, fullWidth, card1H);

    const pad = 3;
    const lineInset = 3; // Inset for divider lines so they don't touch card borders
    let leftX = margin + pad;
    let rightX = margin + colW + gap + pad;

    // Draw vertical divider line in the middle (inset from card borders)
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + (gap/2), yPos + lineInset, margin + colW + (gap/2), yPos + card1H - lineInset);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(11);
    doc.setTextColor(...darkBlue);
    doc.text("Client Details", leftX, yPos + 5);
    doc.text("Intermediary Details", rightX, yPos + 5);

    // Borrower type above client name (e.g. "Solo Trader", "Ltd Co. / Company X", "Partnership")
    const ownershipType = (clientDetails.ownershipType || clientDetails.ownership_type || "").trim();
    const companyName = (clientDetails.companyName || clientDetails.company_name || "").trim();
    const isLimited = /limited|ltd|pvt\s*ltd|llp/i.test(ownershipType);
    const toTitleCase = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    let borrowerTypeLabel = "";
    if (ownershipType) {
      if (isLimited && companyName) {
        borrowerTypeLabel = `Ltd Co. / ${companyName}`;
      } else if (isLimited) {
        borrowerTypeLabel = "Ltd Co.";
      } else if (/sole\s*proprietorship/i.test(ownershipType)) {
        borrowerTypeLabel = "Solo Trader";
      } else if (/partnership/i.test(ownershipType)) {
        borrowerTypeLabel = "Partnership";
      } else if (/pension|trust/i.test(ownershipType)) {
        borrowerTypeLabel = "Pension";
      } else {
        borrowerTypeLabel = toTitleCase(ownershipType);
      }
    }

    const clientRows = [
      ["Client Name", clientDetails.name || ""],
      ["Telephone", clientDetails.telephone || ""],
      ["Email", clientDetails.email || ""],
      ["Security Address", clientDetails.propertyAddress || ""]
    ];
    let ty = yPos + 14; // Extra spacing between section header and first row
    if (borrowerTypeLabel) {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(10);
      doc.setTextColor(...teal);
      doc.text(borrowerTypeLabel, leftX, yPos + 11);
      ty = yPos + 17;
    }
    const clientLabelValueGap = 0; // Space between header and value
    clientRows.forEach(([label, value]) => {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(8);
      doc.setTextColor(...darkBlue);
      const labelStr = `${label}: `;
      doc.text(labelStr, leftX, ty);
      const labelWidth = doc.getTextWidth(labelStr);
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(...teal);
      const maxW = colW - pad * 2 - labelWidth - clientLabelValueGap;
      const lines = value ? doc.splitTextToSize(String(value), maxW) : [""];
      lines.forEach((line) => {
        doc.text(line, leftX + labelWidth + clientLabelValueGap, ty);
        ty += 4;
      });
    });

    ty = yPos + 14; // Extra spacing between section header and first line
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    const interParagraph1 = "Dynesourcer Ltd. is an Introducing Appointed Representative (IAR) of Dynamic Commercial Finance Ltd and does not recommend a specific product after assessing your needs and circumstances.";
    const interLinesAfterBreak = [
      "Dynamic Commercial Finance Ltd",
      "FCA: 946483 | Dynamic Commercial Finance is a broker not a lender.",
      "Top Barn One, Lamport Manor",
      "Lamport, Northamptonshire NN6 9HF",
      "Tel: 0203 978 6758 | admin@dynamiccf.co.uk"
    ];
    doc.splitTextToSize(interParagraph1, colW - pad * 2).forEach((s) => {
      doc.text(s, rightX, ty);
      ty += 4;
    });
    ty += 5; // Paragraph break after "circumstances."
    interLinesAfterBreak.forEach((line) => {
      const split = doc.splitTextToSize(line, colW - pad * 2);
      split.forEach((s) => {
        doc.text(s, rightX, ty);
        ty += 4;
      });
    });

    yPos += card1H + gap;

    // ---- CARD 2: Main Features | Interest Rates & Other Costs ----
    const today = new Date();
    const untilDate = new Date(today);
    const termYears = Number(safeData.productTermYears);
    if (!Number.isNaN(termYears) && termYears > 0) untilDate.setFullYear(today.getFullYear() + termYears);
    const untilStr = untilDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });

    const mainFeatures = [
      ["Product ID", safeData.productId],
      ["Product Name", safeData.productName],
      ["Loan Amount", safeData.loanAmountDisplay],
      ["Mortgage Term", `${safeData.mortgageTerm} years`],
      ["Repayment Method", safeData.repaymentMethodDisplay],
      ["Product Type", safeData.productTypeDisplay],
      ["Arrangement Fee", safeData.arrangementFeeDisplay],
      ["Total Amount to be Paid", safeData.totalAmountPaidDisplay],
      ["Value of Property Assumed", safeData.propertyValueDisplay],
      ["Loan to Value (LTV)", safeData.ltvDisplay]
    ];
    const interestRates = [
      ["APRC", safeData.aprcDisplay],
      ["Fixed Rate", `${safeData.productRateDisplay} until ${untilStr}`],
      ["Thereafter SVR", `currently ${safeData.svrDisplay}`],
      ["Product Fee", (safeData.arrangementFeeDisplay.split(" or ")[0] || safeData.arrangementFeeDisplay) + " (non-refundable)"],
      ["Broker Fee", "Up to 1% (non-refundable)"]
    ];

    const card2Rows = Math.max(mainFeatures.length, interestRates.length);
    const card2RowH = 4.8;
    const card2BottomPad = 6;
    const card2H = 8 + card2Rows * card2RowH + card2BottomPad;
    drawCard(margin, yPos, fullWidth, card2H);

    // Draw vertical divider line in the middle (inset from card borders)
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + (gap/2), yPos + lineInset, margin + colW + (gap/2), yPos + card2H - lineInset);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Main Features of the Loan", margin + pad, yPos + 5);
    doc.text("Interest Rates & Other Costs", margin + colW + gap + pad, yPos + 5);

    const labelW = 42;
    const mainFeaturesValueInset = 12; // Space from divider line
    const mainFeaturesColRight = margin + colW - pad - mainFeaturesValueInset;
    let leftY = yPos + 8;
    let rightY = yPos + 8;
    for (let i = 0; i < card2Rows; i++) {
      if (mainFeatures[i]) {
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(8);
        doc.setTextColor(...darkBlue);
        doc.text(mainFeatures[i][0] + ":", leftX, leftY + 3);
        doc.setFont(fontFamily, "normal");
        doc.setTextColor(...teal);
        const v = doc.splitTextToSize(String(mainFeatures[i][1]), colW - labelW - 4);
        const vArr = Array.isArray(v) ? v : [v];
        let vy = leftY + 3;
        vArr.forEach((line) => {
          doc.text(line, mainFeaturesColRight - doc.getTextWidth(line), vy);
          vy += 4;
        });
        leftY += card2RowH + Math.max(0, (vArr.length - 1) * 4);
      }
      if (interestRates[i]) {
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(8);
        doc.setTextColor(...darkBlue);
        doc.text(interestRates[i][0] + ":", rightX, rightY + 3);
        doc.setFont(fontFamily, "normal");
        doc.setTextColor(...teal);
        const v = doc.splitTextToSize(String(interestRates[i][1]), colW - labelW - 4);
        doc.text(v, rightX + labelW, rightY + 3);
        rightY += card2RowH;
      }
    }
    yPos += card2H + gap;

    // ---- CARD 3: Amount of Each Instalment | Frequency & Number of Payments ----
    const payments = [
      [`${safeData.productTermMonths} monthly payments at ${safeData.productRateDisplay}`, safeData.fixedMonthlyPaymentDisplay],
      ...(!safeData.isLifetimeProduct
        ? [[`Followed by ${safeData.remainingTermMonths} monthly payments at ${safeData.svrDisplay}`, safeData.variableMonthlyPaymentDisplay]]
        : [])
    ];
    const card3RowSpacing = 5.5;
    const card3H = 34;
    drawCard(margin, yPos, fullWidth, card3H);

    // Draw vertical divider line in the middle (inset from card borders)
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + (gap/2), yPos + lineInset, margin + colW + (gap/2), yPos + card3H - lineInset);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Amount of Each Instalment", margin + pad, yPos + 5);
    doc.text("Frequency & Number of Payments", margin + colW + gap + pad, yPos + 5);

    let instY = yPos + 10;
    payments.forEach(([label, amount]) => {
      doc.setFont(fontFamily, "normal");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      
      // Create combined text to keep on same line
      const combinedText = `${label} ${amount}`;
      const textWidth = doc.getTextWidth(combinedText);
      const availableWidth = colW - pad * 2;
      
      // If text is too long, reduce font size
      if (textWidth > availableWidth) {
        doc.setFontSize(7);
      }
      
      // Display label in black and amount in teal on same line
      doc.setTextColor(0, 0, 0);
      doc.text(label, leftX, instY);
      const labelWidth = doc.getTextWidth(label + " ");
      doc.setTextColor(...teal);
      doc.text(amount, leftX + labelWidth, instY);
      
      doc.setFontSize(8);
      instY += card3RowSpacing;
    });
    doc.setFont(fontFamily, "italic");
    doc.setFontSize(7);
    doc.setTextColor(255, 0, 0); // Red color for warning text
    doc.text("Your income may change and interest rates may rise or fall.", leftX, instY + 2);

    const freqRows = [
      ["Payment Frequency", "Monthly"],
      ["Number of Payments", String(safeData.numberOfPayments)]
    ];
    const freqRowSpacing = 5;
    rightY = yPos + 10;
    freqRows.forEach(([k, v]) => {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(8);
      doc.setTextColor(...darkBlue);
      doc.text(k + ":", rightX, rightY + 3);
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(...teal);
      doc.text(v, rightX + 38, rightY + 3);
      rightY += freqRowSpacing;
    });
    yPos += card3H + gap;

    // ---- Early Repayment Charges table with card background ----
    yPos += 8; // Extra space between upper card and ERC heading
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Early Repayment Charges", margin, yPos);
    yPos += 5;

    if (safeData.ercData && safeData.ercData.length > 0) {
      const colWidths = [36, 48, 46, 50];
      const ercRowH = 5.5;
      const headers = ["Loan Amount", "Basis of the charge", "Date of repayment", "Maximum amount of charge"];
      const tableWidth = colWidths.reduce((a, b) => a + b, 0);
      const totalRows = safeData.ercData.length;
      const cardHeight = ercRowH + (totalRows * ercRowH);
      const cardY = yPos;
      const radius = 3;
      
      // Draw card background with rounded top-right corner (path fill + stroke)
      doc.setFillColor(...cardBg);
      doc.setDrawColor(...borderGrey);
      doc.setLineWidth(0.5);
      const k = 0.552; // bezier approximation for quarter circle
      const ercPath = [
        { op: "m", c: [margin, cardY + cardHeight] },
        { op: "l", c: [margin, cardY] },
        { op: "l", c: [margin + tableWidth - radius, cardY] },
        { op: "c", c: [
          margin + tableWidth - radius + radius * k, cardY,
          margin + tableWidth, cardY + radius - radius * k,
          margin + tableWidth, cardY + radius
        ]},
        { op: "l", c: [margin + tableWidth, cardY + cardHeight] },
        { op: "l", c: [margin, cardY + cardHeight] },
        { op: "h", c: [] }
      ];
      doc.path(ercPath);
      doc.fillStroke("FD");

      // Draw header row with light teal background (rounded top-right to match card)
      doc.setFillColor(204, 241, 237); // Light teal/mint color
      const headerPath = [
        { op: "m", c: [margin, yPos + ercRowH] },
        { op: "l", c: [margin, yPos] },
        { op: "l", c: [margin + tableWidth - radius, yPos] },
        { op: "c", c: [
          margin + tableWidth - radius + radius * k, yPos,
          margin + tableWidth, yPos + radius - radius * k,
          margin + tableWidth, yPos + radius
        ]},
        { op: "l", c: [margin + tableWidth, yPos + ercRowH] },
        { op: "l", c: [margin, yPos + ercRowH] },
        { op: "h", c: [] }
      ];
      doc.path(headerPath);
      doc.fill();
      
      let hx = margin;
      headers.forEach((h, i) => {
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(8);
        doc.setTextColor(...darkBlue);
        const lines = doc.splitTextToSize(h, colWidths[i] - 4);
        doc.text(lines, hx + 2, yPos + 3.8);
        hx += colWidths[i];
      });
      yPos += ercRowH;

      // Draw data rows with no background and only horizontal lines
      doc.setFont(fontFamily, "normal");
      safeData.ercData.forEach((erc, idx) => {
        // Draw only horizontal line at bottom of row
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.1);
        doc.line(margin, yPos + ercRowH, margin + tableWidth, yPos + ercRowH);

        // Add text without any cell backgrounds or vertical dividers
        doc.setFontSize(8);
        doc.setTextColor(...teal);
        doc.text(erc.loanAmount, margin + 2, yPos + 3.8);
        doc.setTextColor(0, 0, 0);
        doc.text(erc.basis, margin + colWidths[0] + 2, yPos + 3.8);
        doc.text(erc.until, margin + colWidths[0] + colWidths[1] + 2, yPos + 3.8);
        doc.setTextColor(...teal);
        doc.text(erc.maxCharge, margin + colWidths[0] + colWidths[1] + colWidths[2] + 2, yPos + 3.8);
        yPos += ercRowH;
      });
      yPos += 4;
    }

    // ---- FOOTER SECTION: Position footer cards with proper spacing from ERC table ----
    const footerHeight = 26;
    const disclaimerHeight = 14; // Space needed for disclaimer text
    const gapAfterERC = 4; // Gap after ERC table before footer cards
    const gapBetweenCardsAndDisclaimer = 6; // Gap between footer cards and disclaimer (increased from 4 to 6)
    
    // Add gap after ERC table
    yPos += gapAfterERC;
    
    // Store position for footer cards
    const footerCardsY = yPos;
    
    // Calculate where disclaimer should start (after footer cards + gap)
    const disclaimerStartY = footerCardsY + footerHeight + gapBetweenCardsAndDisclaimer;

    // LEFT BLOCK: Additional Features (Dark Blue Background)
    doc.setFillColor(...darkBlue);
    doc.setDrawColor(...darkBlue);
    doc.roundedRect(margin, footerCardsY, colW, footerHeight, 4, 4, "FD");

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text("Additional Features", margin + 4, footerCardsY + 5);

    const leftFeatureList = [
      { k: "Valuation:", v: safeData.valuation },
      { k: "Legals:", v: safeData.legals },
      { k: "Overpayments:", v: safeData.overpayment }
    ];
    const rightFeatureList = [
      { k: "Portability:", v: safeData.portability },
      { k: "Cashback:", v: safeData.cashback },
      { k: "Underpayments:", v: safeData.underpayments }
    ];

    doc.setFontSize(6.5);
    let featY = footerCardsY + 10;
    leftFeatureList.forEach((item) => {
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(255, 255, 255);
      doc.text(`${item.k}`, margin + 4, featY);
      doc.setTextColor(...teal);
      const labelWidth = doc.getTextWidth(`${item.k} `);
      doc.text(item.v, margin + 4 + labelWidth + 2, featY);
      featY += 3.2;
    });

    featY = footerCardsY + 10;
    rightFeatureList.forEach((item) => {
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(255, 255, 255);
      doc.text(`${item.k}`, margin + (colW / 2) + 2, featY);
      doc.setTextColor(...teal);
      const labelWidth = doc.getTextWidth(`${item.k} `);
      doc.text(item.v, margin + (colW / 2) + 2 + labelWidth + 2, featY);
      featY += 3.2;
    });

    // RIGHT BLOCK: Contact Details (yellow card, teal heading text)
    const rightBlockX = margin + colW + gap;
    const contactCardCenterX = rightBlockX + colW / 2;
    doc.setFillColor(...yellow);
    doc.setDrawColor(...yellow);
    doc.roundedRect(rightBlockX, footerCardsY, colW, footerHeight, 4, 4, "FD");

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(12);
    doc.setTextColor(...darkBlue);
    doc.text("CONTACT DETAILS", contactCardCenterX, footerCardsY + 6, { align: "center" });

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(9);
    doc.setTextColor(...darkBlue);
    doc.text("Josh Mander", contactCardCenterX, footerCardsY + 12, { align: "center" });
    doc.text("admin@dynamicCF.co.uk", contactCardCenterX, footerCardsY + 17, { align: "center" });

    // ---- Footer: disclaimer fixed at bottom of page (red text) ----
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(8);
    doc.setTextColor(255, 0, 0); // Red for disclaimer
    const disclaimerText = "This illustration is for guidance only and subject to application, underwriting and credit approval. Your property may be repossessed if you do not keep up repayments on your mortgage.";
    const disclaimerLines = doc.splitTextToSize(disclaimerText, fullWidth);
    const disclaimerLineHeight = 4;
    let disclaimerY = disclaimerStartY;
    disclaimerLines.forEach((line) => {
      doc.text(line, margin, disclaimerY);
      disclaimerY += disclaimerLineHeight;
    });

    const filename = `Mortgage_Illustration_${clientDetails.name || "Client"}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error("PDF generation error:", error);
  }
};

// IF EOR: illustration-style page 1 + IF research comparison pages.
export const generateInvoiceFinanceEvidenceOfResearchPDF = (
  data,
  clientDetails = {},
  logoBase64 = null,
  fontFiles = null,
  eorData = {}
) => {
  try {
    const toLenderInitial = (raw) => {
      const input = String(raw || "").trim();
      if (!input) return "";
      const local = input.includes("@") ? input.split("@")[0] : input;
      const cleaned = local.replace(/[^a-zA-Z0-9]+/g, " ").trim();
      if (!cleaned) return "";
      const parts = cleaned.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return parts.map((p) => p[0]).join("").toUpperCase();
      }
      return cleaned.toUpperCase();
    };

    const doc = createIllustrationPdf();
    const fontNormal = fontFiles?.normal && isValidBase64ForVFS(fontFiles.normal) ? fontFiles.normal : null;
    const fontBold = fontFiles?.bold && isValidBase64ForVFS(fontFiles.bold) ? fontFiles.bold : null;
    if (fontNormal) {
      try {
        doc.addFileToVFS("BrandonGrotesque-Regular.ttf", fontNormal);
        doc.addFont("BrandonGrotesque-Regular.ttf", ILLUSTRATION_FONT, "normal");
      } catch (e) {
        console.warn("Brandon Grotesque normal font registration failed:", e);
      }
    }
    if (fontBold) {
      try {
        doc.addFileToVFS("BrandonGrotesque-Bold.ttf", fontBold);
        doc.addFont("BrandonGrotesque-Bold.ttf", ILLUSTRATION_FONT, "bold");
      } catch (e) {
        console.warn("Brandon Grotesque bold font registration failed:", e);
      }
    }
    const fontFamily = ILLUSTRATION_FONT_FALLBACK;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const gap = 4;
    let yPos = 6;

    const darkBlue = [52, 73, 94];
    const teal = [79, 209, 197];
    const yellow = [255, 242, 0];
    const cardBg = [255, 255, 255];
    const borderGrey = [226, 232, 240];
    const fullWidth = pageWidth - margin * 2;

    const drawCard = (x, y, w, h) => {
      doc.setDrawColor(...borderGrey);
      doc.setLineWidth(0.5);
      doc.setFillColor(...cardBg);
      doc.roundedRect(x, y, w, h, 3, 3, "FD");
    };

    yPos = drawIllustrationHeader(doc, {
      fontFamily,
      darkBlue,
      teal,
      margin,
      pageWidth,
      yPos,
      logoBase64,
      titleLines: ["INVOICE FINANCE", "EVIDENCE OF RESEARCH"],
    });

    const colW = (fullWidth - gap) / 2;
    const pad = 3;
    const lineInset = 3;
    const leftX = margin + pad;
    const rightX = margin + colW + gap + pad;

    const ownershipType = (clientDetails.ownershipType || clientDetails.ownership_type || "").trim();
    const companyName = (clientDetails.companyName || clientDetails.company_name || "").trim();
    const isLimited = /limited|ltd|pvt\s*ltd|llp/i.test(ownershipType);
    const toTitleCase = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    let borrowerTypeLabel = "";
    if (ownershipType) {
      if (isLimited && companyName) borrowerTypeLabel = `Ltd Co. / ${companyName}`;
      else if (isLimited) borrowerTypeLabel = "Ltd Co.";
      else if (/sole\s*proprietorship/i.test(ownershipType)) borrowerTypeLabel = "Solo Trader";
      else if (/partnership/i.test(ownershipType)) borrowerTypeLabel = "Partnership";
      else if (/pension|trust/i.test(ownershipType)) borrowerTypeLabel = "Pension";
      else borrowerTypeLabel = toTitleCase(ownershipType);
    }

    const interLineH = 3.6;
    const interBottomPad = 5;
    const interStartOffset = 12;
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(7.5);
    const interParagraph1 =
      "Dynesourcer Ltd. is an Introducing Appointed Representative (IAR) of Dynamic Commercial Finance Ltd and does not recommend a specific product after assessing your needs and circumstances.";
    const interLinesAfterBreak = [
      "Dynamic Commercial Finance Ltd",
      "FCA: 946483 | Dynamic Commercial Finance is a broker not a lender.",
      "Top Barn One, Lamport Manor",
      "Lamport, Northamptonshire NN6 9HF",
      "Tel: 0203 978 6758 | admin@dynamiccf.co.uk",
    ];
    const interParaLines = doc.splitTextToSize(interParagraph1, colW - pad * 2);
    let interContentH = interStartOffset + interParaLines.length * interLineH + 3;
    interLinesAfterBreak.forEach((line) => {
      interContentH += doc.splitTextToSize(line, colW - pad * 2).length * interLineH;
    });
    interContentH += interBottomPad;

    const clientBlockH = (borrowerTypeLabel ? 17 : 14) + 3 * 4 + 6;
    const card1H = Math.max(48, Math.ceil(Math.max(clientBlockH, interContentH)));
    drawCard(margin, yPos, fullWidth, card1H);
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + gap / 2, yPos + lineInset, margin + colW + gap / 2, yPos + card1H - lineInset);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(11);
    doc.setTextColor(...darkBlue);
    doc.text("Client Details", leftX, yPos + 5);
    doc.text("Intermediary Details", rightX, yPos + 5);

    const clientRows = [
      ["Client Name", clientDetails.name || ""],
      ["Telephone", clientDetails.telephone || ""],
      ["Email", clientDetails.email || ""],
    ];
    let ty = yPos + 14;
    if (borrowerTypeLabel) {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(10);
      doc.setTextColor(...teal);
      doc.text(borrowerTypeLabel, leftX, yPos + 11);
      ty = yPos + 17;
    }
    clientRows.forEach(([label, value]) => {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(8);
      doc.setTextColor(...darkBlue);
      const labelStr = `${label}: `;
      doc.text(labelStr, leftX, ty);
      const labelWidth = doc.getTextWidth(labelStr);
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(...teal);
      const maxW = colW - pad * 2 - labelWidth;
      const lines = value ? doc.splitTextToSize(String(value), maxW) : [""];
      lines.forEach((line) => {
        doc.text(line, leftX + labelWidth, ty);
        ty += 4;
      });
    });

    ty = yPos + interStartOffset;
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    interParaLines.forEach((s) => {
      doc.text(s, rightX, ty);
      ty += interLineH;
    });
    ty += 3;
    interLinesAfterBreak.forEach((line) => {
      doc.splitTextToSize(line, colW - pad * 2).forEach((s) => {
        doc.text(s, rightX, ty);
        ty += interLineH;
      });
    });
    yPos += card1H + gap;

    const mainFeatures = [
      ["Product ID", data.productId || "—"],
      ["Sector", data.sector || "—"],
      ["Facility Required", data.facilityRequiredDisplay || "—"],
      ["Term Contract", data.contractTermDisplay || "—"],
      ["Net Advance", data.netAdvanceDisplay || "—"],
      ["Facility Type", data.facilityTypeDisplay || "—"],
      ["Cost over product term", data.costOverProductTermDisplay || "—"],
      ["Min. Service Fee", data.minServiceFeeDisplay || "—"],
    ];
    const interestRates = [
      ["IP", data.ipDisplay || "—"],
      ["Service Fee", data.serviceFeeDisplay || "—"],
      ["Discount rate", data.discountRateDisplay || "—"],
      ["APR", data.aprDisplay || "—"],
      ["Arrangement fee", data.arrangementFeeDisplay || "—"],
    ];

    const card2Rows = Math.max(mainFeatures.length, interestRates.length);
    const card2RowH = 4.8;
    const card2H = 8 + card2Rows * card2RowH + 6;
    drawCard(margin, yPos, fullWidth, card2H);
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + gap / 2, yPos + lineInset, margin + colW + gap / 2, yPos + card2H - lineInset);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Main Features of the Facility", margin + pad, yPos + 5);
    doc.text("Interest Rates & Other Costs", margin + colW + gap + pad, yPos + 5);

    drawTwoColumnLabelRows(doc, {
      fontFamily,
      darkBlue,
      teal,
      leftX,
      rightX,
      startY: yPos + 8,
      leftRows: mainFeatures,
      rightRows: interestRates,
      colW,
      pad,
      rowH: card2RowH,
    });
    yPos += card2H + gap;

    const ab = data.additionalBorrowing || {};
    const abRows = data.hasAdditionalBorrowing
      ? [
          ["Loan amount", ab.loanAmountDisplay || "—"],
          ["Interest rate", ab.interestRateDisplay || "—"],
          ["First 12 months interest", ab.first12MonthsInterestDisplay || "—"],
          ["Arrangement fee", ab.arrangementFeeDisplay || "—"],
          ["Contract term", ab.contractTermDisplay || "—"],
          ["Monthly repayment", ab.monthlyRepaymentDisplay || "—"],
          ["Total repayable", ab.totalRepayableDisplay || "—"],
          ["Cost over product term", ab.costOverTermDisplay || "—"],
        ]
      : [];
    const abRowH = 5;
    const card3H = data.hasAdditionalBorrowing ? 10 + abRows.length * abRowH + 4 : 28;
    drawCard(margin, yPos, fullWidth, card3H);

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Additional Borrowing", margin + pad, yPos + 6);
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(8);
    doc.setTextColor(...teal);
    doc.text("Capital & Interest", margin + pad + 48, yPos + 6);

    if (data.hasAdditionalBorrowing) {
      const mid = Math.ceil(abRows.length / 2);
      drawTwoColumnLabelRows(doc, {
        fontFamily,
        darkBlue,
        teal,
        leftX,
        rightX,
        startY: yPos + 10,
        leftRows: abRows.slice(0, mid),
        rightRows: abRows.slice(mid),
        colW,
        pad,
        rowH: abRowH,
        labelW: 52,
      });
    } else {
      doc.setFont(fontFamily, "normal");
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        "Not available for this product. Additional borrowing is only shown for Capital Repayment products.",
        margin + pad,
        yPos + 16
      );
    }
    yPos += card3H + 4;

    const footerHeight = 22;
    const gapBetweenCardsAndDisclaimer = 6;
    const bottomMarginAfterDisclaimer = 14;
    const disclaimerLineH = 4;
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(8);
    const disclaimerText =
      "This evidence of research is for guidance only and subject to application, underwriting and credit approval. Facility terms, fees and availability may change.";
    const disclaimerLines = doc.splitTextToSize(disclaimerText, fullWidth);
    const disclaimerBlockH = Math.max(disclaimerLines.length, 1) * disclaimerLineH;

    let footerCardsY = yPos;
    let disclaimerY = footerCardsY + footerHeight + gapBetweenCardsAndDisclaimer;
    const spaceAfterDisclaimer = pageHeight - (disclaimerY + disclaimerBlockH);
    if (spaceAfterDisclaimer > bottomMarginAfterDisclaimer) {
      const shift = spaceAfterDisclaimer - bottomMarginAfterDisclaimer;
      footerCardsY += shift;
      disclaimerY += shift;
    }

    doc.setFillColor(...darkBlue);
    doc.setDrawColor(...darkBlue);
    doc.roundedRect(margin, footerCardsY, colW, footerHeight, 4, 4, "FD");
    const leftBlockCenterX = margin + colW / 2;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text("Need Help?", leftBlockCenterX, footerCardsY + 8, { align: "center" });
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(9);
    doc.text("Speak to our adviser for next steps.", leftBlockCenterX, footerCardsY + 14, {
      align: "center",
    });

    const rightBlockX = margin + colW + gap;
    const contactCardCenterX = rightBlockX + colW / 2;
    doc.setFillColor(...yellow);
    doc.setDrawColor(...yellow);
    doc.roundedRect(rightBlockX, footerCardsY, colW, footerHeight, 4, 4, "FD");
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(12);
    doc.setTextColor(...darkBlue);
    doc.text("CONTACT DETAILS", contactCardCenterX, footerCardsY + 6, { align: "center" });
    doc.setFontSize(9);
    doc.text("Josh Mander", contactCardCenterX, footerCardsY + 12, { align: "center" });
    doc.text("admin@dynamicCF.co.uk", contactCardCenterX, footerCardsY + 17, { align: "center" });

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(8);
    doc.setTextColor(255, 0, 0);
    disclaimerLines.forEach((line) => {
      doc.text(line, margin, disclaimerY);
      disclaimerY += disclaimerLineH;
    });

    // ---- Page 2+: Filters + IF search results ----
    const allSearchResults = Array.isArray(eorData?.searchResults) ? eorData.searchResults : [];
    const lenderNames = Array.isArray(eorData?.lenderNames) ? eorData.lenderNames : [];
    const selectedProductId = String(eorData?.selectedProductId || data.productId || "").trim();
    const selectedIdNorm = selectedProductId.toLowerCase();
    const appliedFilters = eorData?.appliedFilters || {};
    const EOR_TOP_N = 50;
    const rowId = (row) =>
      String(row?.name || row?.product_id || row?.id || "").trim().toLowerCase();
    const seenIds = new Set();
    const searchResults = [];
    for (const row of allSearchResults) {
      if (searchResults.length >= EOR_TOP_N) break;
      const id = rowId(row);
      if (id) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      searchResults.push(row);
    }

    const fmtMoney = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        maximumFractionDigits: 0,
      }).format(n);
    };
    const fmtPct = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      if (Number.isInteger(n)) return `${n}%`;
      return `${Math.round(n * 100) / 100}%`;
    };
    const fmtServiceFee = (v) => {
      const n = resolveIfServiceFeePercent(v);
      return n == null ? "—" : formatIfServiceFeePercent(n);
    };
    const resolveIfRate = (row) =>
      firstFinite(row?.apr, row?.ratePct, row?.rate, row?.discount_rate_percent, row?.product_rate_percent);
    const resolveIfIp = (row) =>
      firstFinite(row?.invoicePurchasePct, row?.ip_percent, row?.ipPercent);
    const resolveIfService = (row) =>
      firstFinite(
        row?.annualCostPct,
        row?.service_fee_percent,
        row?.serviceFeePct,
        row?.simple_service_fee
      );
    const resolveIfTerm = (row) => {
      const months = firstFinite(
        row?.termMonths,
        row?.contract_term_months,
        row?.contract_term,
        row?.product_termsyears != null ? Number(row.product_termsyears) * 12 : null
      );
      return Number.isFinite(months) && months > 0 ? `${Math.round(months)} mo` : "—";
    };
    const resolveIfNetAdvance = (row) =>
      firstFinite(row?.netAdvance, row?.net_advance);
    const resolveIfCost = (row) =>
      firstFinite(
        row?.costOverProductTerm,
        row?.cost_over_product_term,
        row?.calc_selected?.cost_over_product_term,
        row?.total_cost
      );

    doc.addPage();
    let p2Y = 12;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(16);
    doc.setTextColor(...darkBlue);
    doc.text("FILTERS USED", margin, p2Y);
    p2Y += 8;

    const p2Headers = [
      "Lender",
      "Product",
      "APR",
      "IP",
      "Service Fee",
      "Term",
      "Net Advance",
      "Cost over term",
    ];
    const p2Cols = [24, 34, 14, 12, 22, 14, 26, 30];
    const p2TableW = p2Cols.reduce((a, b) => a + b, 0);
    const rowH = 5;

    const filtersObj =
      appliedFilters?.filters && typeof appliedFilters.filters === "object"
        ? appliedFilters.filters
        : {};
    const namedFilters = Object.entries(filtersObj)
      .filter(
        ([, val]) =>
          val !== null &&
          val !== undefined &&
          val !== "" &&
          val !== 0 &&
          val !== false &&
          (!Array.isArray(val) || val.length > 0)
      )
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`);

    const summaryLine1 = [
      `Sector: ${appliedFilters?.sector || data.sector || "—"}`,
      `Facility: ${fmtMoney(appliedFilters?.facility_required)}`,
      `Turnover: ${fmtMoney(appliedFilters?.projected_turnover)}`,
      `Contract: ${appliedFilters?.contract_term_months || "—"} mo`,
      `Facility type: ${appliedFilters?.facility_type || data.facilityTypeDisplay || "—"}`,
    ].join("  |  ");

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(7.2);
    doc.setTextColor(...darkBlue);
    doc.text("Filters used:", margin, p2Y);
    doc.setFont(fontFamily, "normal");
    doc.setTextColor(60, 60, 60);
    const line1Wrapped = doc.splitTextToSize(summaryLine1, p2TableW - 2);
    doc.text(line1Wrapped, margin + 16, p2Y);
    p2Y += Math.max(4, line1Wrapped.length * 3.4);
    if (namedFilters.length > 0) {
      const line2 = `Additional: ${namedFilters.join("  |  ")}`;
      const line2Wrapped = doc.splitTextToSize(line2, p2TableW - 2);
      doc.text(line2Wrapped, margin, p2Y);
      p2Y += Math.max(4, line2Wrapped.length * 3.4);
    }
    p2Y += 6;

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(16);
    doc.setTextColor(...darkBlue);
    doc.text("SEARCH RESULTS", margin, p2Y);
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text("The Top 50 invoice finance products displayed", margin, p2Y + 4);
    p2Y += 10;

    const drawSearchTableHeader = () => {
      doc.setFillColor(204, 241, 237);
      doc.rect(margin, p2Y, p2TableW, rowH + 1, "F");
      let x = margin;
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(7);
      doc.setTextColor(...darkBlue);
      p2Headers.forEach((h, i) => {
        doc.text(doc.splitTextToSize(h, p2Cols[i] - 2), x + 1, p2Y + 3.5);
        x += p2Cols[i];
      });
      p2Y += rowH + 1;
    };
    drawSearchTableHeader();

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(6.8);
    searchResults.forEach((row) => {
      if (p2Y > pageHeight - 12) {
        doc.addPage();
        p2Y = 12;
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(12);
        doc.setTextColor(...darkBlue);
        doc.text("SEARCH RESULTS (Continued)", margin, p2Y);
        p2Y += 8;
        drawSearchTableHeader();
      }
      const id = rowId(row);
      const isSelected = selectedIdNorm && id === selectedIdNorm;
      if (isSelected) {
        doc.setFillColor(255, 250, 200);
        doc.rect(margin, p2Y, p2TableW, rowH, "F");
      }
      const values = [
        toLenderNameOnly(row?.lenderName || row?.lender_name || row?.lender || row?.applicant || ""),
        row?.product_name || row?.productName || row?.name || row?.id || "—",
        fmtPct(resolveIfRate(row)),
        fmtPct(resolveIfIp(row)),
        fmtServiceFee(resolveIfService(row)),
        resolveIfTerm(row),
        formatCurrency(resolveIfNetAdvance(row)),
        formatCurrency(resolveIfCost(row)),
      ];
      let cx = margin;
      values.forEach((v, i) => {
        const txt = String(v ?? "—");
        const clipped = doc.splitTextToSize(txt, p2Cols[i] - 2)[0] || "—";
        const isMoneyCol = i >= 6;
        doc.setTextColor(isMoneyCol ? teal[0] : 0, isMoneyCol ? teal[1] : 0, isMoneyCol ? teal[2] : 0);
        doc.text(clipped, cx + 1, p2Y + 3.4);
        cx += p2Cols[i];
      });
      p2Y += rowH;
    });

    // Page 3: Lender panel
    doc.addPage();
    let p3Y = 14;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(14);
    doc.setTextColor(...darkBlue);
    doc.text("LENDER PANEL", margin, p3Y);
    p3Y += 5;
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(`Lenders - ${lenderNames.length} Total`, margin, p3Y);
    p3Y += 3;

    const boxX = margin;
    const boxY = p3Y;
    const boxW = fullWidth;
    const boxH = pageHeight - p3Y - 16;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...borderGrey);
    doc.roundedRect(boxX, boxY, boxW, boxH, 3, 3, "FD");

    doc.setFillColor(204, 241, 237);
    doc.rect(boxX + 1, boxY + 1, boxW - 2, 6, "F");
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(8);
    doc.setTextColor(...darkBlue);
    doc.text("Lender", boxX + 4, boxY + 4.5);

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(7.4);
    doc.setTextColor(0, 0, 0);
    const cols = 4;
    const innerPadX = 4;
    const innerPadTop = 11;
    const usableW = boxW - innerPadX * 2;
    const lenderColW = usableW / cols;
    const maxRowsPerCol = Math.max(1, Math.floor((boxH - innerPadTop - 4) / 4.2));
    const maxItems = cols * maxRowsPerCol;
    const shown = lenderNames.map(toLenderInitial).filter(Boolean).slice(0, maxItems);

    shown.forEach((name, idx) => {
      const col = Math.floor(idx / maxRowsPerCol);
      const row = idx % maxRowsPerCol;
      const x = boxX + innerPadX + col * lenderColW;
      const y = boxY + innerPadTop + row * 4.2;
      const text = doc.splitTextToSize(String(name), lenderColW - 1.5)[0] || "";
      doc.text(text, x, y);
    });

    if (lenderNames.length === 0) {
      doc.setTextColor(120, 120, 120);
      doc.text("No lenders available.", boxX + 5, boxY + 12);
    }

    const safeName = String(clientDetails.name || "Client").replace(/[^\w\- ]+/g, "").trim() || "Client";
    doc.save(`Evidence_of_Research_IF_${safeName}.pdf`);
  } catch (error) {
    console.error("IF EOR PDF generation error:", error);
  }
};

// EOR variant: reuses illustration layout and adds pages 2-3 sections.
export const generateEvidenceOfResearchPDF = (
  data,
  clientDetails = {},
  logoBase64 = null,
  fontFiles = null,
  eorData = {}
) => {
  if (data?.isInvoiceFinance) {
    return generateInvoiceFinanceEvidenceOfResearchPDF(
      data,
      clientDetails,
      logoBase64,
      fontFiles,
      eorData
    );
  }
  try {
    const toLenderInitial = (raw) => {
      const input = String(raw || "").trim();
      if (!input) return "";
      const local = input.includes("@") ? input.split("@")[0] : input;
      const cleaned = local.replace(/[^a-zA-Z0-9]+/g, " ").trim();
      if (!cleaned) return "";
      const parts = cleaned.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return parts.map((p) => p[0]).join("").toUpperCase();
      }
      return cleaned.toUpperCase();
    };

    const doc = createIllustrationPdf();
    const fontNormal = fontFiles?.normal && isValidBase64ForVFS(fontFiles.normal) ? fontFiles.normal : null;
    const fontBold = fontFiles?.bold && isValidBase64ForVFS(fontFiles.bold) ? fontFiles.bold : null;
    if (fontNormal) {
      try {
        doc.addFileToVFS("BrandonGrotesque-Regular.ttf", fontNormal);
        doc.addFont("BrandonGrotesque-Regular.ttf", ILLUSTRATION_FONT, "normal");
      } catch (e) {
        console.warn("Brandon Grotesque normal font registration failed:", e);
      }
    }
    if (fontBold) {
      try {
        doc.addFileToVFS("BrandonGrotesque-Bold.ttf", fontBold);
        doc.addFont("BrandonGrotesque-Bold.ttf", ILLUSTRATION_FONT, "bold");
      } catch (e) {
        console.warn("Brandon Grotesque bold font registration failed:", e);
      }
    }
    const fontFamily = ILLUSTRATION_FONT_FALLBACK;

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const gap = 4;
    let yPos = 6;

    const parsedProductTermMonths = Number(data.productTermMonths);
    const parsedProductTermYears = Number(data.productTermYears);
    const parsedMortgageTermYears = Number(data.mortgageTerm);
    const fallbackMortgageMonths = Number.isFinite(parsedMortgageTermYears) && parsedMortgageTermYears > 0
      ? Math.round(parsedMortgageTermYears * 12)
      : 300;
    const isLifetimeProduct = String(data.productTermYears ?? "").trim().toLowerCase() === "lifetime";
    const resolvedProductTermMonths = isLifetimeProduct
      ? fallbackMortgageMonths
      : Number.isFinite(parsedProductTermMonths)
        ? Math.round(parsedProductTermMonths)
        : Number.isFinite(parsedProductTermYears) && parsedProductTermYears > 0
          ? Math.round(parsedProductTermYears * 12)
          : 24;

    const safeData = {
      productId: data.productId || "—",
      productName: data.productName || "—",
      loanAmountDisplay: data.loanAmountDisplay || "—",
      mortgageTerm: data.mortgageTerm || 25,
      repaymentMethodDisplay: data.repaymentMethodDisplay || "Repayment",
      productTypeDisplay: data.productTypeDisplay || "Fixed",
      arrangementFeeDisplay: data.arrangementFeeDisplay || "—",
      totalAmountPaidDisplay: data.totalAmountPaidDisplay || "—",
      propertyValueDisplay: data.propertyValueDisplay || "—",
      ltvDisplay: data.ltvDisplay || "—",
      aprcDisplay: data.aprcDisplay || "—",
      productRateDisplay: data.productRateDisplay || "—",
      svrDisplay: data.svrDisplay || "—",
      productTermYears: data.productTermYears || 2,
      isLifetimeProduct,
      productTermMonths: resolvedProductTermMonths,
      fixedMonthlyPaymentDisplay: data.fixedMonthlyPaymentDisplay || "—",
      variableMonthlyPaymentDisplay: data.variableMonthlyPaymentDisplay || "—",
      remainingTermMonths: isLifetimeProduct ? 0 : (data.remainingTermMonths || 0),
      numberOfPayments: data.numberOfPayments || 300,
      ercData: data.ercData || [],
      valuation: data.valuation || "TBC",
      legals: data.legals || "TBC",
      overpayment: data.overpayment || "No",
      portability: data.portability || "No",
      cashback: data.cashback || "Not specified",
      underpayments: data.underpayments || "Not specified"
    };

    const allSearchResults = Array.isArray(eorData?.searchResults) ? eorData.searchResults : [];
    const lenderNames = Array.isArray(eorData?.lenderNames) ? eorData.lenderNames : [];
    const selectedProductId = String(eorData?.selectedProductId || safeData.productId || "").trim();
    const appliedFilters = eorData?.appliedFilters || {};
    const parseProductTermYears = (row) => {
      const raw = row?.product_termsyears ?? row?.product_term_years ?? row?.product_term;
      const num = Number(raw);
      return Number.isFinite(num) ? num : null;
    };
    const selectedIdNorm = String(selectedProductId || "").trim().toLowerCase();
    const selectedRow = allSearchResults.find((row) => {
      const id = String(row?.name || row?.product_id || "").trim().toLowerCase();
      return selectedIdNorm && id === selectedIdNorm;
    });
    const selectedProductTermYears = parseProductTermYears(selectedRow);
    const highlightedProductTermYears = Number(safeData?.productTermYears);
    const targetProductTermYears = Number.isFinite(selectedProductTermYears)
      ? selectedProductTermYears
      : (Number.isFinite(highlightedProductTermYears) ? highlightedProductTermYears : null);
    const termMatchedResults = Number.isFinite(targetProductTermYears)
      ? allSearchResults.filter((row) => parseProductTermYears(row) === targetProductTermYears)
      : [];
    // Prefer same product-term matches, then fill from the wider rate-ranked pool up to 50.
    // Previously we only used term matches, so EOR often showed ~9 rows instead of top 50.
    const EOR_TOP_N = 50;
    const rowId = (row) => String(row?.name || row?.product_id || "").trim().toLowerCase();
    const seenIds = new Set();
    const searchResults = [];
    const pushUnique = (rows) => {
      for (const row of rows) {
        if (searchResults.length >= EOR_TOP_N) break;
        const id = rowId(row);
        if (id) {
          if (seenIds.has(id)) continue;
          seenIds.add(id);
        }
        searchResults.push(row);
      }
    };
    pushUnique(termMatchedResults);
    pushUnique(allSearchResults);

    const darkBlue = [52, 73, 94];
    const teal = [79, 209, 197];
    const cardBg = [255, 255, 255];
    const borderGrey = [226, 232, 240];
    const tableRowAlt = [230, 248, 248];

    const fullWidth = pageWidth - margin * 2;

    const drawCard = (x, y, w, h) => {
      doc.setDrawColor(...borderGrey);
      doc.setLineWidth(0.5);
      doc.setFillColor(...cardBg);
      doc.roundedRect(x, y, w, h, 3, 3, "FD");
    };

    const logoH = 20;
    const logoW = 52;
    const headerH = Math.max(24, logoH + 4);
    const headerCenterY = yPos + headerH / 2;
    const titleX = margin;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(13);
    doc.setTextColor(...darkBlue);
    doc.text("EVIDENCE OF RESEARCH", titleX, headerCenterY - 1);
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text("Research summary", titleX, headerCenterY + 4);

    const logoX = pageWidth - margin - logoW;
    const logoY = yPos + (headerH - logoH) / 2 - 3;
    if (logoBase64) {
      try {
        addPdfLogoImage(doc, logoBase64, logoX, logoY, logoW, logoH);
      } catch {
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(11);
        doc.setTextColor(...teal);
        doc.text("Dynesourcer", logoX, yPos + 8);
      }
    }
    yPos += headerH + gap;

    const colW = (fullWidth - gap) / 2;
    const card1H = 58;
    drawCard(margin, yPos, fullWidth, card1H);
    const pad = 3;
    const lineInset = 3;
    const leftX = margin + pad;
    const rightX = margin + colW + gap + pad;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(11);
    doc.setTextColor(...darkBlue);
    doc.text("Client Details", leftX, yPos + 5);

    const clientRows = [
      ["Client Name", clientDetails.name || ""],
      ["Telephone", clientDetails.telephone || ""],
      ["Email", clientDetails.email || ""],
      ["Security Address", clientDetails.propertyAddress || ""]
    ];
    let ty = yPos + 14;
    clientRows.forEach(([label, value]) => {
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(8);
      doc.setTextColor(...darkBlue);
      const labelStr = `${label}: `;
      doc.text(labelStr, leftX, ty);
      const labelWidth = doc.getTextWidth(labelStr);
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(...teal);
      const lines = value ? doc.splitTextToSize(String(value), fullWidth - pad * 2 - labelWidth - 2) : [""];
      lines.forEach((line) => {
        doc.text(line, leftX + labelWidth, ty);
        ty += 4;
      });
    });
    yPos += card1H + gap;

    const today = new Date();
    const untilDate = new Date(today);
    const termYears = Number(safeData.productTermYears);
    if (!Number.isNaN(termYears) && termYears > 0) untilDate.setFullYear(today.getFullYear() + termYears);
    const untilStr = untilDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });

    const mainFeatures = [
      ["Product ID", safeData.productId],
      ["Product Name", safeData.productName],
      ["Loan Amount", safeData.loanAmountDisplay],
      ["Mortgage Term", `${safeData.mortgageTerm} years`],
      ["Repayment Method", safeData.repaymentMethodDisplay],
      ["Product Type", safeData.productTypeDisplay],
      ["Arrangement Fee", safeData.arrangementFeeDisplay],
      ["Total Amount to be Paid", safeData.totalAmountPaidDisplay],
      ["Value of Property Assumed", safeData.propertyValueDisplay],
      ["Loan to Value (LTV)", safeData.ltvDisplay]
    ];
    const interestRates = [
      ["APRC", safeData.aprcDisplay],
      ["Fixed Rate", `${safeData.productRateDisplay} until ${untilStr}`],
      ["Thereafter SVR", `currently ${safeData.svrDisplay}`],
      ["Product Fee", (safeData.arrangementFeeDisplay.split(" or ")[0] || safeData.arrangementFeeDisplay) + " (non-refundable)"],
      ["Broker Fee", "Up to 1% (non-refundable)"]
    ];
    const card2Rows = Math.max(mainFeatures.length, interestRates.length);
    const card2RowH = 4.8;
    const card2H = 8 + card2Rows * card2RowH + 6;
    drawCard(margin, yPos, fullWidth, card2H);
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + (gap / 2), yPos + lineInset, margin + colW + (gap / 2), yPos + card2H - lineInset);
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Main Features of the Loan", margin + pad, yPos + 5);
    doc.text("Interest Rates & Other Costs", margin + colW + gap + pad, yPos + 5);
    let leftY = yPos + 8;
    let rightY = yPos + 8;
    for (let i = 0; i < card2Rows; i++) {
      if (mainFeatures[i]) {
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(8);
        doc.setTextColor(...darkBlue);
        doc.text(mainFeatures[i][0] + ":", leftX, leftY + 3);
        doc.setFont(fontFamily, "normal");
        doc.setTextColor(...teal);
        doc.text(String(mainFeatures[i][1]), margin + colW - 14 - doc.getTextWidth(String(mainFeatures[i][1])), leftY + 3);
        leftY += card2RowH;
      }
      if (interestRates[i]) {
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(8);
        doc.setTextColor(...darkBlue);
        doc.text(interestRates[i][0] + ":", rightX, rightY + 3);
        doc.setFont(fontFamily, "normal");
        doc.setTextColor(...teal);
        doc.text(String(interestRates[i][1]), rightX + 38, rightY + 3);
        rightY += card2RowH;
      }
    }
    yPos += card2H + gap;

    const card3H = 34;
    drawCard(margin, yPos, fullWidth, card3H);
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.line(margin + colW + (gap / 2), yPos + lineInset, margin + colW + (gap / 2), yPos + card3H - lineInset);
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Amount of Each Instalment", margin + pad, yPos + 5);
    doc.text("Frequency & Number of Payments", margin + colW + gap + pad, yPos + 5);
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(`${safeData.productTermMonths} monthly payments at ${safeData.productRateDisplay}`, leftX, yPos + 12);
    doc.setTextColor(...teal);
    doc.text(`${safeData.fixedMonthlyPaymentDisplay}`, leftX, yPos + 17);
    if (!safeData.isLifetimeProduct) {
      doc.setTextColor(0, 0, 0);
      doc.text(`Followed by ${safeData.remainingTermMonths} monthly payments at ${safeData.svrDisplay}`, leftX, yPos + 22);
      doc.setTextColor(...teal);
      doc.text(`${safeData.variableMonthlyPaymentDisplay}`, leftX, yPos + 27);
    }
    doc.setFont(fontFamily, "bold");
    doc.setTextColor(...darkBlue);
    doc.text("Payment Frequency:", rightX, yPos + 12);
    doc.text("Number of Payments:", rightX, yPos + 17);
    doc.setFont(fontFamily, "normal");
    doc.setTextColor(...teal);
    doc.text("Monthly", rightX + 38, yPos + 12);
    doc.text(String(safeData.numberOfPayments), rightX + 38, yPos + 17);
    yPos += card3H + gap + 8;

    // ---- Early Repayment Charges table with same layout as Illustration ----
    yPos += 8;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(...darkBlue);
    doc.text("Early Repayment Charges", margin, yPos);
    yPos += 5;

    if (safeData.ercData && safeData.ercData.length > 0) {
      const colWidths = [36, 48, 46, 50];
      const ercRowH = 5.5;
      const headers = ["Loan Amount", "Basis of the charge", "Date of repayment", "Maximum amount of charge"];
      const tableWidth = colWidths.reduce((a, b) => a + b, 0);
      const totalRows = safeData.ercData.length;
      const cardHeight = ercRowH + (totalRows * ercRowH);
      const cardY = yPos;
      const radius = 3;
      const k = 0.552;

      doc.setFillColor(...cardBg);
      doc.setDrawColor(...borderGrey);
      doc.setLineWidth(0.5);
      const ercPath = [
        { op: "m", c: [margin, cardY + cardHeight] },
        { op: "l", c: [margin, cardY] },
        { op: "l", c: [margin + tableWidth - radius, cardY] },
        { op: "c", c: [
          margin + tableWidth - radius + radius * k, cardY,
          margin + tableWidth, cardY + radius - radius * k,
          margin + tableWidth, cardY + radius
        ]},
        { op: "l", c: [margin + tableWidth, cardY + cardHeight] },
        { op: "l", c: [margin, cardY + cardHeight] },
        { op: "h", c: [] }
      ];
      doc.path(ercPath);
      doc.fillStroke("FD");

      doc.setFillColor(204, 241, 237);
      const headerPath = [
        { op: "m", c: [margin, yPos + ercRowH] },
        { op: "l", c: [margin, yPos] },
        { op: "l", c: [margin + tableWidth - radius, yPos] },
        { op: "c", c: [
          margin + tableWidth - radius + radius * k, yPos,
          margin + tableWidth, yPos + radius - radius * k,
          margin + tableWidth, yPos + radius
        ]},
        { op: "l", c: [margin + tableWidth, yPos + ercRowH] },
        { op: "l", c: [margin, yPos + ercRowH] },
        { op: "h", c: [] }
      ];
      doc.path(headerPath);
      doc.fill();

      let hx = margin;
      headers.forEach((h, i) => {
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(8);
        doc.setTextColor(...darkBlue);
        const lines = doc.splitTextToSize(h, colWidths[i] - 4);
        doc.text(lines, hx + 2, yPos + 3.8);
        hx += colWidths[i];
      });
      yPos += ercRowH;

      doc.setFont(fontFamily, "normal");
      safeData.ercData.forEach((erc) => {
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.1);
        doc.line(margin, yPos + ercRowH, margin + tableWidth, yPos + ercRowH);

        doc.setFontSize(8);
        doc.setTextColor(...teal);
        doc.text(String(erc.loanAmount || "—"), margin + 2, yPos + 3.8);
        doc.setTextColor(0, 0, 0);
        doc.text(String(erc.basis || "—"), margin + colWidths[0] + 2, yPos + 3.8);
        doc.text(String(erc.until || "—"), margin + colWidths[0] + colWidths[1] + 2, yPos + 3.8);
        doc.setTextColor(...teal);
        doc.text(String(erc.maxCharge || "—"), margin + colWidths[0] + colWidths[1] + colWidths[2] + 2, yPos + 3.8);
        yPos += ercRowH;
      });
      yPos += 4;
    }

    // ---- Footer card: Additional Features (same as Illustration left card) ----
    const footerHeight = 26;
    yPos += 4;
    doc.setFillColor(...darkBlue);
    doc.setDrawColor(...darkBlue);
    doc.roundedRect(margin, yPos, colW, footerHeight, 4, 4, "FD");

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text("Additional Features", margin + 4, yPos + 5);

    const leftFeatureList = [
      { k: "Valuation:", v: safeData.valuation },
      { k: "Legals:", v: safeData.legals },
      { k: "Overpayments:", v: safeData.overpayment }
    ];
    const rightFeatureList = [
      { k: "Portability:", v: safeData.portability },
      { k: "Cashback:", v: safeData.cashback },
      { k: "Underpayments:", v: safeData.underpayments }
    ];

    doc.setFontSize(6.5);
    let featY = yPos + 10;
    leftFeatureList.forEach((item) => {
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(255, 255, 255);
      doc.text(`${item.k}`, margin + 4, featY);
      doc.setTextColor(...teal);
      const labelWidth = doc.getTextWidth(`${item.k} `);
      doc.text(item.v, margin + 4 + labelWidth + 2, featY);
      featY += 3.2;
    });

    featY = yPos + 10;
    rightFeatureList.forEach((item) => {
      doc.setFont(fontFamily, "normal");
      doc.setTextColor(255, 255, 255);
      doc.text(`${item.k}`, margin + (colW / 2) + 2, featY);
      doc.setTextColor(...teal);
      const labelWidth = doc.getTextWidth(`${item.k} `);
      doc.text(item.v, margin + (colW / 2) + 2 + labelWidth + 2, featY);
      featY += 3.2;
    });

    // Page 2: Filters Used + Search Results
    doc.addPage();
    let p2Y = 12;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(16);
    doc.setTextColor(...darkBlue);
    doc.text("FILTERS USED", margin, p2Y);
    p2Y += 8;

    const fmtMoney = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        maximumFractionDigits: 0,
      }).format(n);
    };
    const p2Headers = [
      "Lender",
      "Product",
      "Rate",
      "Product Type",
      "Product Term",
      "Monthly payment",
      "Cost over product term",
      "total fees",
    ];
    const p2Cols = [24, 34, 14, 22, 18, 24, 28, 22];
    const p2TableW = p2Cols.reduce((a, b) => a + b, 0);
    const rowH = 5;

    const filtersObj = appliedFilters?.filters && typeof appliedFilters.filters === "object" ? appliedFilters.filters : {};
    const namedFilters = Object.entries(filtersObj)
      .filter(([, val]) => val !== null && val !== undefined && val !== "" && (!Array.isArray(val) || val.length > 0))
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`);
    const summaryLine1 = [
      `Property Value: ${fmtMoney(appliedFilters?.property_value)}`,
      `Deposit: ${fmtMoney(appliedFilters?.deposit_amount)}`,
      `Mortgage: ${fmtMoney(appliedFilters?.mortgage_amount)}`,
      `Est. Rent: ${fmtMoney(appliedFilters?.estimated_rent)}`,
      `Term: ${appliedFilters?.mortgage_term_years || "—"} yrs`,
      `Repayment: ${appliedFilters?.repayment_type || "—"}`,
    ].join("  |  ");
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(7.2);
    doc.setTextColor(...darkBlue);
    doc.text("Filters used:", margin, p2Y);
    doc.setFont(fontFamily, "normal");
    doc.setTextColor(60, 60, 60);
    const line1Wrapped = doc.splitTextToSize(summaryLine1, p2TableW - 2);
    doc.text(line1Wrapped, margin + 16, p2Y);
    p2Y += Math.max(4, line1Wrapped.length * 3.4);
    if (namedFilters.length > 0) {
      const line2 = `Additional: ${namedFilters.join("  |  ")}`;
      const line2Wrapped = doc.splitTextToSize(line2, p2TableW - 2);
      doc.text(line2Wrapped, margin, p2Y);
      p2Y += Math.max(4, line2Wrapped.length * 3.4);
    }
    p2Y += 6;

    doc.setFont(fontFamily, "bold");
    doc.setFontSize(16);
    doc.setTextColor(...darkBlue);
    doc.text("SEARCH RESULTS", margin, p2Y);
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text("The Top 50 products results displayed", margin, p2Y + 4);
    p2Y += 10;
    const drawSearchTableHeader = () => {
      doc.setFillColor(204, 241, 237);
      doc.rect(margin, p2Y, p2TableW, rowH + 1, "F");
      let x = margin;
      doc.setFont(fontFamily, "bold");
      doc.setFontSize(7);
      doc.setTextColor(...darkBlue);
      p2Headers.forEach((h, i) => {
        doc.text(doc.splitTextToSize(h, p2Cols[i] - 2), x + 1, p2Y + 3.5);
        x += p2Cols[i];
      });
      p2Y += rowH + 1;
    };
    drawSearchTableHeader();

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(6.8);
    searchResults.forEach((row) => {
      if (p2Y > pageHeight - 12) {
        doc.addPage();
        p2Y = 12;
        doc.setFont(fontFamily, "bold");
        doc.setFontSize(12);
        doc.setTextColor(...darkBlue);
        doc.text("SEARCH RESULTS (Continued)", margin, p2Y);
        p2Y += 8;
        drawSearchTableHeader();
      }
      const id = String(row?.name || row?.product_id || "").trim().toLowerCase();
      const isSelected = selectedIdNorm && id === selectedIdNorm;
      if (isSelected) {
        doc.setFillColor(255, 250, 200);
        doc.rect(margin, p2Y, p2TableW, rowH, "F");
      }
      const calc = resolveCalcForTable(row);
      const values = [
        toLenderNameOnly(row?.lender_name || row?.lender || row?.applicant || ""),
        row?.product_name || "—",
        formatPercent(row?.product_rate_percent),
        toProductTypeDisplay(row),
        toProductTermDisplay(row),
        formatCurrency(calc?.monthly_payment),
        formatCurrency(calc?.cost_over_product_term),
        formatCurrency(calc?.total_fees),
      ];
      let cx = margin;
      values.forEach((v, i) => {
        const txt = String(v ?? "—");
        const clipped = doc.splitTextToSize(txt, p2Cols[i] - 2)[0] || "—";
        doc.setTextColor(i >= 5 ? teal[0] : 0, i >= 5 ? teal[1] : 0, i >= 5 ? teal[2] : 0);
        doc.text(clipped, cx + 1, p2Y + 3.4);
        cx += p2Cols[i];
      });
      p2Y += rowH;
    });

    // Page 3: Lender panel (names only)
    doc.addPage();
    let p3Y = 14;
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(14);
    doc.setTextColor(...darkBlue);
    doc.text("LENDER PANEL", margin, p3Y);
    p3Y += 5;
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(`Lenders - ${lenderNames.length} Total`, margin, p3Y);
    p3Y += 3;

    const boxX = margin;
    const boxY = p3Y;
    const boxW = fullWidth;
    const boxH = pageHeight - p3Y - 16;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...borderGrey);
    doc.roundedRect(boxX, boxY, boxW, boxH, 3, 3, "FD");

    // Header row style aligned with search-results header
    doc.setFillColor(204, 241, 237);
    doc.rect(boxX + 1, boxY + 1, boxW - 2, 6, "F");
    doc.setFont(fontFamily, "bold");
    doc.setFontSize(8);
    doc.setTextColor(...darkBlue);
    doc.text("Lender", boxX + 4, boxY + 4.5);

    doc.setFont(fontFamily, "normal");
    doc.setFontSize(7.4);
    doc.setTextColor(0, 0, 0);
    const cols = 4;
    const innerPadX = 4;
    const innerPadTop = 11;
    const usableW = boxW - innerPadX * 2;
    const lenderColW = usableW / cols;
    const maxRowsPerCol = Math.max(1, Math.floor((boxH - innerPadTop - 4) / 4.2));
    const maxItems = cols * maxRowsPerCol;
    const shown = lenderNames.map(toLenderInitial).filter(Boolean).slice(0, maxItems);

    shown.forEach((name, idx) => {
      const col = Math.floor(idx / maxRowsPerCol);
      const row = idx % maxRowsPerCol;
      const x = boxX + innerPadX + (col * lenderColW);
      const y = boxY + innerPadTop + (row * 4.2);
      const text = doc.splitTextToSize(String(name), lenderColW - 1.5)[0] || "";
      doc.text(text, x, y);
    });

    if (lenderNames.length === 0) {
      doc.setTextColor(120, 120, 120);
      doc.text("No lenders available.", boxX + 5, boxY + 12);
    }

    const filename = `Evidence_of_Research_${clientDetails.name || "Client"}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error("EOR PDF generation error:", error);
  }
};

// Default logo for illustration PDF (polar bear + Dynesourcer). Place your PNG in public folder.
const ILLUSTRATION_LOGO_URLS = [
  (typeof process !== "undefined" && process.env?.PUBLIC_URL) ? `${process.env.PUBLIC_URL}/dynesourcer-illustration-logo.png` : "/dynesourcer-illustration-logo.png",
  (typeof process !== "undefined" && process.env?.PUBLIC_URL) ? `${process.env.PUBLIC_URL}/dyne-logo-stacked.png` : "/dyne-logo-stacked.png",
];

/** Load image from URL and return base64 data URL for use in jsPDF (PNG). */
function loadImageAsBase64(url) {
  return new Promise((resolve, reject) => {
    const fullUrl = url.startsWith("http") ? url : `${typeof window !== "undefined" ? window.location.origin : ""}${url}`;
    fetch(fullUrl)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`Failed to load: ${r.status}`))))
      .then((buf) => {
        const bytes = new Uint8Array(buf);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        resolve("data:image/png;base64," + (typeof btoa !== "undefined" ? btoa(binary) : Buffer.from(binary, "binary").toString("base64")));
      })
      .catch(reject);
  });
}

/** Try loading default illustration logo from public URLs; returns base64 or null. */
export async function loadDefaultIllustrationLogo() {
  for (const url of ILLUSTRATION_LOGO_URLS) {
    try {
      const raw = await loadImageAsBase64(url);
      return await optimizeImageForPdf(raw);
    } catch {
      continue;
    }
  }
  return null;
}

/** Return true if str looks like valid raw base64 (no data URL). jsPDF addFileToVFS expects raw base64. */
function isValidBase64ForVFS(str) {
  if (typeof str !== "string" || str.length === 0) return false;
  const trimmed = str.trim();
  if (trimmed.length === 0) return false;
  if (/^data:/i.test(trimmed)) return false;
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  if (!base64Regex.test(trimmed)) return false;
  try {
    if (typeof atob !== "undefined") atob(trimmed);
    return true;
  } catch {
    return false;
  }
}

/** Normalize font value: if it's a data URL, strip to raw base64; otherwise return as-is if valid. */
function normalizeFontBase64(value) {
  if (value == null || typeof value !== "string") return null;
  const s = value.trim();
  const match = s.match(/^data:[^;]+;base64,(.+)$/);
  const raw = match ? match[1].replace(/\s/g, "") : s.replace(/\s/g, "");
  return isValidBase64ForVFS(raw) ? raw : null;
}

/** Try loading Brandon Grotesque font from public/fonts/BrandonGrotesque.json (format: { normal: base64, bold: base64 }). Returns null if not found. */
export async function loadDefaultIllustrationFont() {
  try {
    const path = "/fonts/BrandonGrotesque.json";
    const url = typeof window !== "undefined" ? `${window.location.origin}${path}` : path;
    const res = await fetch(url);
    if (!res.ok) return null;
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) return null;
    const json = await res.json();
    if (!json || typeof json !== "object") return null;
    const normal = normalizeFontBase64(json.normal);
    const bold = normalizeFontBase64(json.bold || json.normal);
    if (normal) return { normal, bold: bold || normal };
    return null;
  } catch {
    return null;
  }
}

/** Generate PDF with default logo loaded from public folder (e.g. dynesourcer-illustration-logo.png with polar bear). */
export async function generateClientIllustrationPDFWithLogo(data, clientDetails = {}, fontFiles = null) {
  const logoBase64 = await loadDefaultIllustrationLogo();
  const fonts = fontFiles ?? await loadDefaultIllustrationFont();
  generateClientIllustrationPDF(data, clientDetails, logoBase64, fonts);
}

export async function generateEvidenceOfResearchPDFWithLogo(
  data,
  clientDetails = {},
  eorData = {},
  fontFiles = null
) {
  const logoBase64 = await loadDefaultIllustrationLogo();
  const fonts = fontFiles ?? await loadDefaultIllustrationFont();
  generateEvidenceOfResearchPDF(data, clientDetails, logoBase64, fonts, eorData);
}